import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Mic, Square, Camera, Image as ImageIcon, Loader2, X, Trash2,
  Video, Sparkles,
  Keyboard, Zap, Sunrise, ChevronDown, Check, Play, Pause,
  Send, ClipboardCheck, Save, RefreshCw, Globe, Link2, Share2, Users,
} from 'lucide-react';
import {
  getPrayer, updatePrayer, replacePrayerImage,
  deletePrayer, replacePrayerAudio, sendGuidedChat, fetchGuidedChatSeed, resetGuidedChatSession, suggestPrayerTitle, checkPerspective, fetchHashtagSuggestionsAI, BACKEND_URL,
} from '../lib/api';
import api from '../lib/api';
import { bgGradient } from '../lib/color';
import { useAuth } from '../contexts/AuthContext';
import SanctuaryModerationDialog, { parseSanctuaryError } from '../components/SanctuaryModerationDialog';
import GalleryPickerModal from '../components/prayer-create/GalleryPickerModal';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';

const MAX_SECONDS = 60; // 1 minute — matches video recording cap

const fmtTime = (s) => {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
};

const SECTIONS = {
  TITLE: 1,
  PRAYER: 2,
  COVER: 3,
  SHARE: 4,
};

/** Accordion section wrapper with number badge + collapse/expand */
const Section = ({ number, title, subtitle, isOpen, onToggle, done, testId, children }) => (
  <section
    data-testid={testId}
    className="rounded-xl border border-zinc-200 dark:border-zinc-800 overflow-hidden mb-3"
  >
    <button
      type="button"
      onClick={onToggle}
      data-testid={`${testId}-toggle`}
      aria-expanded={isOpen}
      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-50 dark:hover:bg-zinc-900/60 transition-colors"
    >
      <span
        className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${
          done
            ? 'bg-amber-500 text-white'
            : isOpen
              ? 'bg-zinc-900 text-white dark:bg-white dark:text-zinc-900'
              : 'bg-zinc-200 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300'
        }`}
      >
        {done ? <Check size={14} /> : number}
      </span>
      <span className="flex-1 text-left">
        <span className="text-sm font-medium block">{title}</span>
        {subtitle && (
          <span
            className="text-xs text-amber-500 dark:text-amber-400 block mt-0.5 font-normal"
            data-testid={`${testId}-subtitle`}
          >
            {subtitle}
          </span>
        )}
      </span>
      <ChevronDown
        size={18}
        className={`text-zinc-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
      />
    </button>
    {isOpen && (
      <div className="px-4 pb-4 pt-1 border-t border-zinc-100 dark:border-zinc-800">
        {children}
      </div>
    )}
  </section>
);


const RadioRow = ({ checked, disabled, onSelect, label, testId, name, indent = 0 }) => (
  <label
    data-testid={`${testId}-row`}
    style={{ paddingLeft: `${indent * 24}px` }}
    className={`flex items-center gap-3 py-2 px-2 rounded-md cursor-pointer transition-colors ${
      disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-zinc-50 dark:hover:bg-zinc-900/60'
    } ${checked ? 'bg-amber-50 dark:bg-amber-950/30' : ''}`}
  >
    <input
      type="radio"
      name={name}
      checked={checked}
      onChange={onSelect}
      disabled={disabled}
      data-testid={testId}
      className="accent-amber-500"
    />
    <span className="text-sm">{label}</span>
  </label>
);

// Visible-hashtag UI kill switch. AI auto-generation, storage on the
// prayer document, and every trending/analytics query still run so
// the tags remain valuable server-side — only the compose-form field
// and downstream display pills are hidden. Flip to `true` to
// re-expose the whole hashtag surface without any other code change.
const SHOW_HASHTAG_UI = false;

const AddPrayer = () => {
  const navigate = useNavigate();
  const { id: editingId } = useParams();
  const isEditing = Boolean(editingId);

  // Auto-launch the Prayer assistance once when the create page first mounts.
  // Skipped for the edit flow (users editing an existing prayer usually
  // just want to change the cover image / visibility, not rewrite the
  // text). A short delay so the page paints first — feels less startling
  // than a modal appearing at t=0.
  // On fresh create, open a method-picker modal instead of auto-firing
  // Prayer Assistance. Users choose Video / Voice / Keyboard / Assistance.
  // The old auto-open behaviour felt startling — this replaces it with a
  // one-tap chooser.
  const [methodPickerOpen, setMethodPickerOpen] = useState(false);
  useEffect(() => {
    if (isEditing) return;
    const timer = setTimeout(() => setMethodPickerOpen(true), 250);
    return () => clearTimeout(timer);
  }, [isEditing]);
  const { user, loading, openAuthModal: login } = useAuth();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [hashtags, setHashtags] = useState([]); // array of clean tag strings, no leading '#'
  // ---- AI hashtag suggestions ----
  // Tracks whether we've already auto-populated hashtags for the
  // current description snapshot so the user isn't overridden after
  // manually editing. `aiHashtagsBusy` powers the "Suggesting…" state
  // on the button + inline hint.
  const [aiHashtagsBusy, setAiHashtagsBusy] = useState(false);
  const [aiHashtagsError, setAiHashtagsError] = useState(null);
  const aiAutoFiredRef = useRef(false);
  const aiLastTextRef = useRef('');
  // Owner-marked silent prayer. When true, viewers see the prayer without
  // any TTS or system-voice playback (the owner's recorded audio, if any,
  // is still respected — silent applies to system fallback narration).
  const [isSilent, setIsSilent] = useState(false);
  // When true, the backend picks a random site member and drops a
  // notification into their inbox linking to this prayer. Only applies
  // to freshly-created prayers — editing an existing prayer never
  // re-fires the fan-out.
  const [shareRandom, setShareRandom] = useState(false);

  // Perspective — captured when Prayer Assistance's draft comes back in
  // the 1st person. We auto-store both the original 1st-person text
  // AND the auto-generated 3rd-person alternate at save time — no user
  // confirmation. Owners always see their original words on the watch
  // page; non-owner viewers see the 3rd-person alternate via the
  // `displayText` swap in WatchPrayer.
  const [perspectiveType, setPerspectiveType] = useState('third_person');
  const [thirdPersonText, setThirdPersonText] = useState('');
  // While true, the perspective-check request is in flight and (if
  // needed) the 3rd-person alternate is being generated. Surfaced as a
  // status message below the read-only prayer text so the user isn't
  // left wondering whether anything else is coming.
  const [isCheckingPerspective, setIsCheckingPerspective] = useState(false);
  // Snapshot of the description text at the moment the perspective was
  // last analyzed (either by the initial Prayer Assistance flow, on
  // load in edit mode, or by a previous re-sync). Used by the blur
  // handler on the prayer textarea to decide whether the user's manual
  // edits warrant a fresh perspective check — we only re-run OpenAI
  // when the text has actually changed since the last analysis.
  const lastCheckedDescriptionRef = useRef('');
  // Snapshot of the transcription text at the moment the current recording
  // was captured (or loaded on edit). Used to detect whether the visible
  // text has drifted from the audio. If it has AND the user has picked
  // "No" for "Use my voice recording for narration", we prevent them from
  // switching back to "Yes" — the recording no longer matches the text.
  const [recordingTextSnapshot, setRecordingTextSnapshot] = useState(null);
  const [hashtagInput, setHashtagInput] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState(null);
  const [existingAudioUrl, setExistingAudioUrl] = useState(null);
  const [loadingExisting, setLoadingExisting] = useState(isEditing);
  const [loadError, setLoadError] = useState(null);

  // Audio saved after dictation, kept for potential Own Voice narration
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioPlaybackUrl, setAudioPlaybackUrl] = useState(null);
  const [audioPlaying, setAudioPlaying] = useState(false);

  // Accordion open section (only one at a time)
  // All sections start open and never auto-close. The user can still toggle
  // an individual section closed by clicking its header, but selecting one
  // section no longer collapses the others.
  const [openSections, setOpenSections] = useState(() => new Set([
    SECTIONS.TITLE, SECTIONS.PRAYER, SECTIONS.COVER, SECTIONS.SHARE,
  ]));
  const isSectionOpen = (n) => openSections.has(n);
  const toggleSection = (n) => setOpenSections((prev) => {
    const next = new Set(prev);
    if (next.has(n)) next.delete(n);
    else next.add(n);
    return next;
  });

  // Creation method — which input path produced the current prayer
  // body. Persisted server-side on save (POST /prayers) and shown in
  // the Manage Prayers > Type column. Set by the individual method
  // handlers: `approveVideo` → 'video', `handleTranscribe` (voice)
  // → 'audio', `applyGuidedReply` (Assistance chat) → 'assistance'.
  // Defaults to 'text' — the user just typed into the textarea. In
  // edit mode we don't override an existing method from the loaded
  // prayer; a re-record inside edit will bump this to the new method.
  const [creationMethod, setCreationMethod] = useState('text');

  // Recorder modal state
  const [recorderOpen, setRecorderOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  // ------------------------------------------------------------------
  // Video-recording flow (max 1 minute, Cloudinary-backed).
  //   `videoRecorderOpen`  → recorder / preview modal is visible
  //   `videoRecording`     → camera stream is capturing right now
  //   `videoBlob`          → captured clip awaiting Retake/Approve
  //   `videoPreviewUrl`    → object URL used in the review <video>
  //   `videoUploading`     → true while POSTing to /prayers/transcribe-video
  //   `videoRemainingSec`  → countdown shown to the user (60 → 0)
  //   `videoUrl`           → final Cloudinary URL (set after Approve)
  // ------------------------------------------------------------------
  const VIDEO_MAX_SECONDS = 60;
  const [videoRecorderOpen, setVideoRecorderOpen] = useState(false);
  const [videoRecording, setVideoRecording] = useState(false);
  const [videoBlob, setVideoBlob] = useState(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState(null);
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoRemainingSec, setVideoRemainingSec] = useState(VIDEO_MAX_SECONDS);
  const [videoUrl, setVideoUrl] = useState(null);
  const videoStreamRef = useRef(null);
  const videoRecorderRef = useRef(null);
  const videoChunksRef = useRef([]);
  const videoTimerRef = useRef(null);
  const videoElRef = useRef(null);
  const [elapsed, setElapsed] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [transcribing, setTranscribing] = useState(false);
  const [recorderError, setRecorderError] = useState(null);

  // Submit
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Sanctuary-moderation surfaced separately from generic errors so we can
  // render the dedicated red modal with the AI's specific reason.
  const [sanctuary, setSanctuary] = useState(null);

  // Guided prayer dialog
  const [guidedOpen, setGuidedOpen] = useState(false);
  // Guided chat state — multi-turn OpenAI conversation. `guidedSessionId` is
  // client-generated; the backend echoes it back and we keep it stable across
  // turns so future streaming/continuity implementations can rely on it.
  const [guidedMessages, setGuidedMessages] = useState([]); // [{role: 'user'|'assistant', content: string}]
  const [guidedInput, setGuidedInput] = useState('');
  const [guidedSending, setGuidedSending] = useState(false);
  const [guidedError, setGuidedError] = useState(null);
  const [guidedSessionId, setGuidedSessionId] = useState(() =>
    (crypto && crypto.randomUUID) ? crypto.randomUUID() : `guided-${Date.now()}`
  );

  // Mobile virtual-keyboard handling for the Prayer Assistance dialog.
  // When the on-screen keyboard opens on iOS/Android, the layout
  // viewport stays fixed but the *visual* viewport shrinks. Radix's
  // default `top-[50%] translate-y-[-50%]` centering is anchored to
  // the layout viewport, so the dialog gets shoved partway off the
  // visible area (and its bottom composer disappears behind the
  // keyboard). We watch `window.visualViewport` while the dialog is
  // open and pin `top` / `maxHeight` to the visible band so the
  // dialog always fits, stays centered, and keeps the composer above
  // the keyboard. Desktop keeps the default centering.
  const [guidedVvStyle, setGuidedVvStyle] = useState({});
  // Refactored so both the visualViewport listener AND the textarea's
  // onFocus handler can force a recompute. `applyGuidedSizing` reads
  // the CURRENT visualViewport (or falls back to window dims if
  // unavailable) and pins the dialog to the visible band. We call it
  // once on open and again on every focus so mobile browsers that
  // don't fire vv.resize promptly (older Chrome Android, some in-app
  // webviews) still get the dialog resized the moment the keyboard is
  // about to appear.
  const applyGuidedSizing = useCallback(() => {
    if (window.innerWidth >= 640) { setGuidedVvStyle({}); return; }
    const vv = window.visualViewport;
    const visibleH = vv ? vv.height : window.innerHeight;
    const visibleW = vv ? vv.width : window.innerWidth;
    const offsetTop = vv ? vv.offsetTop : 0;
    const offsetLeft = vv ? vv.offsetLeft : 0;
    const centerY = offsetTop + visibleH / 2;
    const centerX = offsetLeft + visibleW / 2;
    // Explicitly cap width/max-width so iOS Safari's zoom-on-focus can't
    // shove the dialog wider than the visible viewport (which would
    // introduce horizontal scroll). Also override left/translate so the
    // dialog re-centers on the *visual* viewport, not the layout one.
    const dialogW = Math.min(visibleW - 16, 500);
    setGuidedVvStyle({
      top: `${centerY}px`,
      left: `${centerX}px`,
      width: `${dialogW}px`,
      maxWidth: `${dialogW}px`,
      maxHeight: `${Math.max(240, visibleH - 16)}px`,
    });
  }, []);

  // ---- AI hashtag suggestion helpers ----
  // Merges AI-derived tags into the existing chip list (preserves any
  // tags the user typed manually, dedupes, respects the 10-tag cap).
  const runAiHashtagSuggest = useCallback(async (source = 'manual') => {
    const text = (description || '').trim();
    if (!text || text.length < 20) {
      if (source === 'manual') {
        setAiHashtagsError('Write a bit more prayer text first so we can suggest tags.');
      }
      return;
    }
    setAiHashtagsBusy(true);
    setAiHashtagsError(null);
    try {
      const suggestions = await fetchHashtagSuggestionsAI({
        content: text,
        limit: 6,
        exclude: hashtags,
      });
      const clean = (suggestions || [])
        .map((s) => (typeof s === 'string' ? s : s?.tag))
        .filter(Boolean)
        .map((t) => t.replace(/^#+/, '').toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 32))
        .filter((t) => t && !hashtags.includes(t));
      if (!clean.length) {
        if (source === 'manual') {
          setAiHashtagsError('No new suggestions right now. Try adding more detail to your prayer.');
        }
        return;
      }
      const merged = [...hashtags];
      for (const t of clean) {
        if (merged.length >= 10) break;
        if (!merged.includes(t)) merged.push(t);
      }
      setHashtags(merged);
      aiLastTextRef.current = text;
      aiAutoFiredRef.current = true;
    } catch (err) {
      console.error('AI hashtag suggest failed', err);
      if (source === 'manual') {
        setAiHashtagsError('Could not suggest tags right now. Please try again.');
      }
    } finally {
      setAiHashtagsBusy(false);
    }
  }, [description, hashtags]);

  // Auto-fire ONE-TIME when the prayer text first stabilises after
  // the assistant / transcription flow has populated it. Debounce
  // 1200 ms of quiet on the description, and only if the user hasn't
  // already added any hashtags manually. Subsequent runs need the
  // explicit "Suggest with AI" button, so we never overwrite intent.
  useEffect(() => {
    if (aiAutoFiredRef.current) return;
    if (hashtags.length > 0) return;
    const text = (description || '').trim();
    if (text.length < 40) return;
    if (text === aiLastTextRef.current) return;
    const timer = setTimeout(() => {
      if (aiAutoFiredRef.current) return;
      if (hashtags.length > 0) return;
      runAiHashtagSuggest('auto');
    }, 1200);
    return () => clearTimeout(timer);
  }, [description, hashtags.length, runAiHashtagSuggest]);
  useEffect(() => {
    if (!guidedOpen) { setGuidedVvStyle({}); return undefined; }
    const vv = window.visualViewport;
    applyGuidedSizing();
    if (!vv) return undefined;
    vv.addEventListener('resize', applyGuidedSizing);
    vv.addEventListener('scroll', applyGuidedSizing);
    window.addEventListener('resize', applyGuidedSizing);
    return () => {
      vv.removeEventListener('resize', applyGuidedSizing);
      vv.removeEventListener('scroll', applyGuidedSizing);
      window.removeEventListener('resize', applyGuidedSizing);
    };
  }, [guidedOpen, applyGuidedSizing]);

  // Re-record (audio-replace) status for edit flow
  const [audioReplacing, setAudioReplacing] = useState(false);

  // Share options — three-tier privacy model. Default is `'circle'`
  // ("Unlisted — but shown on my circle"): the prayer stays off Home /
  // Nearby / Random / Search, but the author's own Circle Page and
  // their followers' aggregated circle feed still see it, and anyone
  // with the direct link can view. This is the conservative default —
  // the author explicitly opts UP to public feeds by picking 'public',
  // or DOWN to link-only by picking 'unlisted'.
  const [visibility, setVisibility] = useState('circle'); // 'public' | 'circle' | 'unlisted'
  // Explicit "No image" choice — distinct from "image not yet picked". When
  // true we render the chosen background as the cover.
  // "No image" is the DEFAULT cover choice — most prayers ship as
  // gradient-only text cards. Users can still switch to Camera/Upload/
  // Gallery from the Cover section; the edit-hydration effect flips
  // this to `false` when loading a prayer that already has an image.
  const [noImage, setNoImage] = useState(true);
  // Background color choice for the "No image" mode. `null` = default purple
  // gradient; a `#rrggbb` hex string = solid color of the user's choosing.
  const [backgroundColor, setBackgroundColor] = useState(null);

  // Sunrise/sunset gallery dialog. All internal state (pagination,
  // errors, picking status) lives inside `<GalleryPickerModal>` — the
  // parent only owns the open/closed flag and the pick callback.
  const [galleryOpen, setGalleryOpen] = useState(false);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const textAreaRef = useRef(null);
  const audioPlaybackRef = useRef(null);
  const guidedScrollRef = useRef(null);
  const guidedInputRef = useRef(null);
  // AbortController for the currently-running Speed Writer request. Rotated on
  // every reset so any late reply from a previous session cannot pollute the
  // new session's message list.
  const guidedAbortRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => () => {
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    if (timerRef.current) clearInterval(timerRef.current);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    if (audioPlaybackUrl) URL.revokeObjectURL(audioPlaybackUrl);
    if (imagePreview && imagePreview.startsWith('blob:')) URL.revokeObjectURL(imagePreview);
  }, [previewUrl, audioPlaybackUrl, imagePreview]);

  // Prefill fields when editing an existing prayer.
  useEffect(() => {
    if (!editingId) return;
    let cancelled = false;
    (async () => {
      try {
        const p = await getPrayer(editingId);
        if (cancelled) return;
        setTitle(p.title || '');
        setDescription(p.description || '');
        setHashtags(Array.isArray(p.hashtags) ? p.hashtags : []);
        setIsSilent(Boolean(p.is_silent));
        // Perspective — hydrate from the saved prayer so the 3rd-person
        // alternate card renders under the read-only description exactly
        // as it does on the create flow. The field is read-only in the
        // UI (generated by Prayer Assistance, not editable by hand).
        {
          const savedPerspective = (p.perspective_type === 'first_person' && (p.third_person_text || '').trim())
            ? 'first_person'
            : 'third_person';
          setPerspectiveType(savedPerspective);
          setThirdPersonText(savedPerspective === 'first_person' ? p.third_person_text.trim() : '');
          // Seed the "last checked" baseline with the saved description
          // so the blur-driven re-sync only fires when the user actually
          // edits the text after opening the edit form.
          lastCheckedDescriptionRef.current = p.description || '';
        }
        // On load, treat the current description as in-sync with the
        // existing recording (if any). If the user later edits the text
        // while the toggle is "No", the "Yes" option will lock out.
        if (p.audio_url) {
          setRecordingTextSnapshot(p.description || '');
        } else {
          setRecordingTextSnapshot(null);
        }
        // Narration is deprecated (removed in favour of a future site-wide
        // playback setting); we ignore any legacy narration fields.
        // Preserve the stored visibility across edits. Any unknown legacy
        // value falls back to 'public' so a broken row can't lock the
        // author out of publishing.
        {
          const v = p.visibility;
          setVisibility(v === 'unlisted' || v === 'circle' ? v : 'public');
        }
        // Seed any previously-attached Cloudinary video URL so it
        // stays associated with the prayer through subsequent PATCH
        // saves (edit path can re-record via the video method, which
        // will overwrite this value).
        if (p.video_url) setVideoUrl(p.video_url);
        if (p.image_url) {
          const url = `${BACKEND_URL}${p.image_url}`;
          setExistingImageUrl(url);
          setImagePreview(url);
          // `noImage` defaults to true on mount so the "choose a
          // background color" picker appears for text-only prayers.
          // When the prayer being edited actually HAS a cover image,
          // flip it back so the render branch shows the uploaded
          // image instead of the No-Image state. Without this, edit
          // mode looks like the cover image was lost.
          setNoImage(false);
        } else {
          // No cover image on load — reflect that in the picker so the user
          // can see & change the current gradient color.
          setNoImage(true);
          if (p.background_color) setBackgroundColor(p.background_color);
        }
        if (p.audio_url) {
          setExistingAudioUrl(`${BACKEND_URL}${p.audio_url}`);
          setAudioDuration(p.duration_sec || 0);
        }
      } catch (e) {
        if (!cancelled) setLoadError(e.response?.status === 404 ? 'Prayer not found' : 'Failed to load prayer');
      } finally {
        if (!cancelled) setLoadingExisting(false);
      }
    })();
    return () => { cancelled = true; };
  }, [editingId]);

  // ---------- Recorder ----------
  const resetRecorder = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setRecording(false);
    setElapsed(0);
    setRecorderError(null);
    chunksRef.current = [];
  };

  const openRecorder = () => { resetRecorder(); setRecorderOpen(true); };
  const closeRecorder = () => { resetRecorder(); setRecorderOpen(false); };

  // ---------------- Video recorder ----------------
  const stopVideoStream = () => {
    if (videoStreamRef.current) {
      try {
        videoStreamRef.current.getTracks().forEach((t) => t.stop());
      } catch { /* noop */ }
      videoStreamRef.current = null;
    }
  };

  const resetVideoRecorder = () => {
    if (videoTimerRef.current) { clearInterval(videoTimerRef.current); videoTimerRef.current = null; }
    if (videoRecorderRef.current && videoRecorderRef.current.state !== 'inactive') {
      try { videoRecorderRef.current.stop(); } catch { /* noop */ }
    }
    videoRecorderRef.current = null;
    stopVideoStream();
    if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    setVideoPreviewUrl(null);
    setVideoBlob(null);
    setVideoRecording(false);
    setVideoRemainingSec(VIDEO_MAX_SECONDS);
    videoChunksRef.current = [];
  };

  const openVideoRecorder = () => { resetVideoRecorder(); setVideoRecorderOpen(true); };
  const closeVideoRecorder = () => { resetVideoRecorder(); setVideoRecorderOpen(false); };

  const startVideoRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' },
        audio: true,
      });
      videoStreamRef.current = stream;
      // Show a live preview from the camera in the modal.
      if (videoElRef.current) {
        videoElRef.current.srcObject = stream;
        videoElRef.current.muted = true;
        try { await videoElRef.current.play(); } catch { /* autoplay may need user gesture */ }
      }
      const mimeCandidates = [
        'video/webm;codecs=vp9,opus',
        'video/webm;codecs=vp8,opus',
        'video/webm',
        'video/mp4',
      ];
      const mimeType = mimeCandidates.find((m) => window.MediaRecorder?.isTypeSupported?.(m));
      const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      videoRecorderRef.current = rec;
      videoChunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) videoChunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(videoChunksRef.current, { type: mimeType || 'video/webm' });
        setVideoBlob(blob);
        setVideoPreviewUrl(URL.createObjectURL(blob));
        setVideoRecording(false);
        // Detach the live stream from the preview element so the
        // review step shows the RECORDED clip (not the live camera
        // feed) — a fresh `src` is set via `videoPreviewUrl` binding.
        if (videoElRef.current) {
          try { videoElRef.current.srcObject = null; } catch { /* noop */ }
        }
        stopVideoStream();
      };
      rec.start();
      setVideoRecording(true);
      setVideoRemainingSec(VIDEO_MAX_SECONDS);
      // Countdown / auto-stop at 60s. `setInterval` fires every
      // second and updates the visible timer.
      videoTimerRef.current = setInterval(() => {
        setVideoRemainingSec((s) => {
          if (s <= 1) {
            // Fire stop from inside the tick — MediaRecorder.stop is
            // idempotent so a race with the user pressing Stop is safe.
            try { videoRecorderRef.current?.stop(); } catch { /* noop */ }
            if (videoTimerRef.current) { clearInterval(videoTimerRef.current); videoTimerRef.current = null; }
            return 0;
          }
          return s - 1;
        });
      }, 1000);
    } catch (e) {
      console.error('startVideoRecording failed', e);
      alert('Could not access your camera. Please check permissions and try again.');
      resetVideoRecorder();
    }
  };

  const stopVideoRecording = () => {
    if (videoRecorderRef.current && videoRecorderRef.current.state !== 'inactive') {
      try { videoRecorderRef.current.stop(); } catch { /* noop */ }
    }
    if (videoTimerRef.current) { clearInterval(videoTimerRef.current); videoTimerRef.current = null; }
  };

  const retakeVideo = () => {
    // Wipe the current clip and immediately restart the camera so the
    // user isn't left staring at an empty modal.
    if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    setVideoPreviewUrl(null);
    setVideoBlob(null);
    setVideoRemainingSec(VIDEO_MAX_SECONDS);
    videoChunksRef.current = [];
    startVideoRecording();
  };

  // Extract the first visible frame of a recorded video blob and
  // return it as a JPEG File suitable for the same `imageFile` state
  // slot that the manual "Take photo / Upload / Gallery" flows use.
  // This lets the video-recording path auto-populate a relevant
  // thumbnail without asking the user to pick a separate cover image.
  //
  // Implementation notes:
  //   • Off-screen `<video>` element loads the blob via object URL.
  //     `preload=auto` + `muted` are required so WebKit/Safari will
  //     decode the first frame without needing a user gesture.
  //   • Seek to a tiny offset (0.001s) rather than exactly 0 — some
  //     codecs won't have painted the very first sample by the time
  //     `loadeddata` fires; a hair past 0 forces the decoder to emit
  //     a real keyframe.
  //   • Draws to a canvas sized to the video's natural dimensions
  //     (falls back to 720x1280 if metadata is missing, matching the
  //     portrait recorder default).
  //   • JPEG @ 0.85 quality keeps the cover under a few hundred KB
  //     even for HD frames — plenty for the thumbnail use case.
  //   • On any failure (unsupported codec, empty blob, seek timeout)
  //     the helper resolves `null` rather than throwing, so the
  //     approve flow never breaks the primary transcription path.
  const extractVideoFirstFrame = (blob) => new Promise((resolve) => {
    if (!blob) { resolve(null); return; }
    let objectUrl = null;
    let settled = false;
    const cleanup = () => {
      try { if (objectUrl) URL.revokeObjectURL(objectUrl); } catch { /* noop */ }
    };
    const finish = (result) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(result);
    };
    try {
      objectUrl = URL.createObjectURL(blob);
      const v = document.createElement('video');
      v.preload = 'auto';
      v.muted = true;
      v.playsInline = true;
      v.crossOrigin = 'anonymous';
      // Bail if metadata / seek never resolves — 5s ceiling matches
      // the max time we'd realistically wait before falling back to
      // the default cover.
      const timeout = setTimeout(() => finish(null), 5000);
      v.onerror = () => { clearTimeout(timeout); finish(null); };
      v.onloadeddata = () => {
        // Seek to 1ms past the start so codecs that show a blank
        // first sample paint the first real keyframe instead.
        try { v.currentTime = 0.001; } catch { /* fall through to seeked */ }
      };
      v.onseeked = () => {
        try {
          const w = v.videoWidth || 720;
          const h = v.videoHeight || 1280;
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(v, 0, 0, w, h);
          canvas.toBlob((imgBlob) => {
            clearTimeout(timeout);
            if (!imgBlob) { finish(null); return; }
            const file = new File(
              [imgBlob],
              `video-thumbnail-${Date.now()}.jpg`,
              { type: 'image/jpeg' },
            );
            finish(file);
          }, 'image/jpeg', 0.85);
        } catch (err) {
          clearTimeout(timeout);
          console.warn('extractVideoFirstFrame draw failed', err);
          finish(null);
        }
      };
      v.src = objectUrl;
    } catch (err) {
      console.warn('extractVideoFirstFrame setup failed', err);
      finish(null);
    }
  });

  const approveVideo = async () => {
    if (!videoBlob) return;
    setVideoUploading(true);
    // Kick off first-frame extraction in parallel with the Cloudinary
    // upload — both use the same in-memory blob, and doing them
    // concurrently keeps the total wait short. We capture the blob
    // reference here because `closeVideoRecorder()` will clear it
    // before we settle.
    const blobForFrame = videoBlob;
    const framePromise = extractVideoFirstFrame(blobForFrame);
    try {
      const form = new FormData();
      // MediaRecorder blobs default to webm; give the server a hint.
      form.append('video', videoBlob, 'recording.webm');
      const backend = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backend}/api/prayers/transcribe-video`, {
        method: 'POST',
        credentials: 'include',
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Upload failed' }));
        throw new Error(err.detail || `Upload failed (${res.status})`);
      }
      const data = await res.json();
      console.log('[video-approve] transcribe-video response', { hasTranscript: Boolean(data?.transcript), transcriptLen: (data?.transcript || '').length, hasVideoUrl: Boolean(data?.video_url) });
      setVideoUrl(data.video_url || null);
      const transcript = (data.transcript || '').trim();
      if (transcript) {
        // Reuse the voice-recording lock plumbing: setting
        // `recordingTextSnapshot` marks the text as coming from a
        // transcription, which the textarea readOnly guard treats as
        // "locked while Yes-use-voice is active".
        setDescription(transcript);
        setRecordingTextSnapshot(transcript);
        setCreationMethod('video');
        // Provisional perspective state — matches what
        // `commitPrayerText` does for the Assistance flow so the two
        // paths converge before the async perspective check runs.
        // Clears any stale alternate from a previous draft in the
        // same session (defensive; a fresh video shouldn't inherit
        // stale state from an earlier flow).
        setPerspectiveType('third_person');
        setThirdPersonText('');
        // Auto-suggest a short warm title from the fresh transcript,
        // but ONLY when the title field is still empty — never overwrite
        // whatever the user has already typed. Uses the same
        // suggestPrayerTitle helper that voice recording and the guided
        // assistance flow rely on so all three input methods converge
        // on identical title-suggestion behaviour. Fires in the
        // background; failures are logged and silently ignored so a
        // title-suggestion outage never blocks the transcript from
        // landing on the form.
        if (transcript && !title.trim()) {
          suggestPrayerTitle(transcript)
            .then(({ title: suggested }) => {
              const t = (suggested || '').trim();
              if (!t) return;
              setTitle((prev) => (prev.trim() ? prev : t.slice(0, 200)));
            })
            .catch((err) => { console.warn('video transcript title suggestion failed', err); });
        }
      }
      // Auto-populate the cover image from the recorded video's first
      // frame. Awaits the extraction started in parallel with the
      // upload above. We only overwrite the image slot when the user
      // hasn't already picked one — respects a Take-photo / Gallery /
      // Upload choice made before entering the recorder (unlikely on
      // a fresh Create flow but safe on Edit). Also flips `noImage`
      // off in case the user had previously chosen the color-only
      // cover.
      try {
        const frameFile = await framePromise;
        if (frameFile && !imageFile) {
          setImageFile(frameFile);
          if (imagePreview) URL.revokeObjectURL(imagePreview);
          setImagePreview(URL.createObjectURL(frameFile));
          setNoImage(false);
        }
      } catch (err) {
        // Non-fatal — the prayer will just save without an
        // auto-thumbnail and the user can pick one manually.
        console.warn('video first-frame extraction failed', err);
      }
      closeVideoRecorder();
      // Trigger the same 3rd-person perspective generation the guided
      // Prayer Assistance flow uses — so a recorded video prayer gets
      // its shared-facing alternate drafted automatically once the
      // transcript is in the field. AWAITED here (matches the guided
      // flow's `await runPerspectiveCheck(clean)`) so React commits
      // the description + provisional-perspective batch first, and
      // the subsequent first-person/rewrite state update triggers a
      // clean re-render that reveals the "3rd-person version" card.
      if (transcript) {
        await runPerspectiveCheck(transcript);
      }
    } catch (e) {
      console.error('approveVideo failed', e);
      alert(e.message || 'Could not upload the video. Please try again.');
    } finally {
      setVideoUploading(false);
    }
  };

  // Clean up any camera stream / object URL when the component unmounts.
  useEffect(() => () => {
    stopVideoStream();
    if (videoTimerRef.current) clearInterval(videoTimerRef.current);
    if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
  }, []);

  const startRecording = async () => {
    setRecorderError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : (MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '');
      const options = mimeType ? { mimeType } : undefined;
      const mr = new MediaRecorder(stream, options);
      chunksRef.current = [];
      let recordedDuration = 0;
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType || 'audio/webm' });
        stream.getTracks().forEach(t => t.stop());
        streamRef.current = null;
        handleTranscribe(blob, recordedDuration);
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => {
        setElapsed(prev => {
          const next = prev + 1;
          recordedDuration = next;
          if (next >= MAX_SECONDS) stopRecording();
          return next;
        });
      }, 1000);
    } catch (e) {
      setRecorderError('Microphone access denied or unavailable. Please allow microphone permission and try again.');
      console.error(e);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setRecording(false);
  };

  const handleTranscribe = async (blob, durationSec) => {
    setTranscribing(true);
    setRecorderError(null);
    try {
      // In edit mode we take a different path: upload directly to the audio-
      // replace endpoint so the existing prayer is updated in place with the
      // new audio + fresh transcript + word_timestamps. This closes the modal
      // and refreshes the local existing-audio strip.
      if (isEditing && editingId) {
        setAudioReplacing(true);
        const updated = await replacePrayerAudio(editingId, blob, durationSec || 0);
        setExistingAudioUrl(`${BACKEND_URL}${updated.audio_url}?t=${Date.now()}`);
        setAudioDuration(updated.duration_sec || durationSec || 0);
        // If backend seeded description from transcript (empty case), reflect it.
        if (updated.description && !description.trim()) {
          setDescription(updated.description);
        }
        // Snapshot the current description as the "in-sync" baseline for
        // this new recording. Any subsequent edit will lock out "Yes".
        setRecordingTextSnapshot(updated.description || description || '');
        // A fresh recording resets the toggle back to its default (Yes)
        // so the user starts from an "in sync" state.
        setIsSilent(false);
        // Clear any in-memory blob preview — the server copy is authoritative now.
        setAudioBlob(null);
        if (audioPlaybackUrl) URL.revokeObjectURL(audioPlaybackUrl);
        setAudioPlaybackUrl(null);
        setAudioPlaying(false);
        setAudioReplacing(false);
        setTranscribing(false);
        setRecorderOpen(false);
        resetRecorder();
        return;
      }

      const form = new FormData();
      form.append('audio', blob, `prayer-${Date.now()}.webm`);
      const { data } = await api.post('/prayers/transcribe', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const transcript = (data?.transcript || '').trim().slice(0, 2000);
      setDescription(transcript);
      // Snapshot the transcript as the in-sync baseline for this new
      // recording. Any subsequent edit while "No" is selected will lock
      // out the "Yes" option.
      setRecordingTextSnapshot(transcript);
      setIsSilent(false);
      setCreationMethod('audio');
      // Provisional perspective state — matches `commitPrayerText` so
      // the voice-transcribe flow converges with the guided/video
      // flows before the async perspective check runs. Clears any
      // stale alternate from a previous draft in the same session.
      setPerspectiveType('third_person');
      setThirdPersonText('');

      // Save the audio blob for potential future audio playback
      setAudioBlob(blob);
      setAudioDuration(durationSec || 0);
      if (audioPlaybackUrl) URL.revokeObjectURL(audioPlaybackUrl);
      setAudioPlaybackUrl(URL.createObjectURL(blob));

      setTranscribing(false);
      setRecorderOpen(false);
      resetRecorder();
      setTimeout(() => textAreaRef.current?.focus(), 100);
      // Auto-suggest a short warm title from the fresh transcript,
      // but ONLY when the title field is still empty — never overwrite
      // whatever the user has already typed. Uses the same helper the
      // guided-assistance flow relies on so voice, video, and guided
      // input methods all converge on identical title-suggestion
      // behaviour. Fires in the background; failures are logged and
      // silently ignored so a title-suggestion outage never blocks
      // the transcript from landing on the form.
      if (transcript && !title.trim()) {
        suggestPrayerTitle(transcript)
          .then(({ title: suggested }) => {
            const t = (suggested || '').trim();
            if (!t) return;
            setTitle((prev) => (prev.trim() ? prev : t.slice(0, 200)));
          })
          .catch((err) => { console.warn('voice transcript title suggestion failed', err); });
      }
      // Kick off the shared 3rd-person perspective generation for the
      // fresh transcript — same helper the guided flow and the video
      // recorder use, so all three input methods converge on the same
      // dual-perspective state. AWAITED (matches guided flow) so the
      // provisional-perspective batch is committed before the
      // first-person/rewrite state update fires.
      if (transcript) {
        await runPerspectiveCheck(transcript);
      }
    } catch (e) {
      console.error('Transcribe failed', e);
      // Sanctuary-moderation failures on the audio-replace path surface
      // via the red modal (same as text-save failures).
      const sanct = parseSanctuaryError(e);
      if (sanct) {
        setSanctuary(sanct);
        setRecorderOpen(false);
      } else {
        const msg = e.response?.data?.detail || 'Could not transcribe audio. Please try recording again or type your prayer.';
        setRecorderError(String(msg));
      }
      setTranscribing(false);
      setAudioReplacing(false);
    }
  };

  const removeSavedAudio = () => {
    setAudioBlob(null);
    setAudioDuration(0);
    if (audioPlaybackUrl) URL.revokeObjectURL(audioPlaybackUrl);
    setAudioPlaybackUrl(null);
    setAudioPlaying(false);
    // Removing the recording clears the sync baseline — text is now
    // freely editable and the (hidden) toggle no longer applies.
    setRecordingTextSnapshot(null);
    setIsSilent(false);
  };

  const toggleAudioPlayback = () => {
    if (!audioPlaybackRef.current) return;
    if (audioPlaying) { audioPlaybackRef.current.pause(); setAudioPlaying(false); }
    else { audioPlaybackRef.current.play(); setAudioPlaying(true); }
  };

  // ---------- Image ----------
  const handleImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview(URL.createObjectURL(file));
    setNoImage(false);
  };

  const removeImage = () => {
    setImageFile(null);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview(null);
    // In edit mode, the existing image should be cleared visually so the
    // preview no longer shows it. `existingImageUrl` stays truthy so we know
    // to send `remove_image: true` at publish/PATCH time.
    setNoImage(false);
  };

  // "No image" explicitly chooses the purple-gradient background. Clears any
  // in-progress upload or existing cover; in edit mode this also flags the
  // prayer for backend image removal on save.
  const chooseNoImage = () => {
    setImageFile(null);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview(null);
    setNoImage(true);
  };

  const clearNoImage = () => setNoImage(false);

  // Gallery picker — the modal component (`<GalleryPickerModal>`) owns
  // all internal pagination/loading/error state. The parent only holds
  // the open/closed flag and the pick callback. This alias keeps the
  // existing call sites in the JSX below unchanged.
  const openGalleryDialog = () => setGalleryOpen(true);
  const handleGalleryPick = (file, previewUrl) => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(file);
    setImagePreview(previewUrl);
    setNoImage(false);
  };

  // ---------- Guided chat ----------
  /**
   * Rotate the session id, clear all local chat state, and return a fresh
   * empty history array. Call this before every conversation start so no
   * stale messages leak across opens.
   *
   * Defence in depth:
   *   1. Abort any in-flight guided-chat request so a late reply from a prior
   *      session can't push into the new one.
   *   2. Sweep localStorage / sessionStorage for any speed-writer / guided-*
   *      keys. We don't write any today, but this guarantees a truly clean
   *      slate even if a future feature accidentally caches something.
   *   3. Fire-and-forget the backend /reset ack so the server logs a clean
   *      session boundary (the endpoint itself is a no-op because the chat
   *      handler is stateless).
   */
  const resetGuidedChat = () => {
    // Abort any in-flight request tied to the previous session.
    if (guidedAbortRef.current) {
      try { guidedAbortRef.current.abort(); } catch { /* noop */ }
    }
    guidedAbortRef.current = new AbortController();

    // Defensive browser-storage sweep. Match any key containing "guided",
    // "speedwriter", or "speed-writer" (case-insensitive).
    const speedWriterKeyRe = /guided|speed[-_ ]?writer/i;
    try {
      const toRemove = [];
      for (let i = 0; i < localStorage.length; i += 1) {
        const k = localStorage.key(i);
        if (k && speedWriterKeyRe.test(k)) toRemove.push(k);
      }
      toRemove.forEach((k) => localStorage.removeItem(k));
    } catch { /* localStorage may be unavailable in some contexts */ }
    try {
      const toRemove = [];
      for (let i = 0; i < sessionStorage.length; i += 1) {
        const k = sessionStorage.key(i);
        if (k && speedWriterKeyRe.test(k)) toRemove.push(k);
      }
      toRemove.forEach((k) => sessionStorage.removeItem(k));
    } catch { /* sessionStorage may be unavailable in some contexts */ }

    const newId = (crypto && crypto.randomUUID) ? crypto.randomUUID() : `guided-${Date.now()}`;
    setGuidedSessionId(newId);
    setGuidedMessages([]);
    setGuidedInput('');
    setGuidedError(null);
    setGuidedSending(false);

    // Fire-and-forget backend reset ack. Failures are silent — the endpoint is
    // an observability hook, not a correctness dependency.
    resetGuidedChatSession(newId).catch(() => { /* ignore */ });

    return newId;
  };

  // Prevents useEffect below from double-firing the seed request if React
  // renders twice with guidedOpen=true (e.g. StrictMode or re-render caused
  // by other state updates). Refs never trigger re-renders, and the flag is
  // cleared in resetGuidedChat so subsequent opens work.
  const guidedSeedFiredRef = useRef(false);

  /**
   * SINGLE ENTRY POINT for starting a Speed Writer session. Every UI trigger
   * that offers Speed Writer — the Zap icon in the "Your prayer" header, the
   * "Yes" button in the help-prompt popup, and any future entry — MUST call
   * this function (never a bespoke variant) so they share identical behaviour:
   *
   *   1. resetGuidedChat() → new UUID, cleared messages/input/error/sending,
   *      aborted in-flight requests, wiped speed-writer browser-storage keys,
   *      fired the backend /reset audit ack.
   *   2. setGuidedOpen(true) → the modal renders.
   *   3. useEffect below fetches the hidden seed prompt from
   *      /api/prayers/guided-chat/seed and sends it via sendGuidedTurn with
   *      isSeed=true so the backend guarantees a two-message LLM call.
   *
   * Do NOT duplicate this logic in a button onClick. Wrap-around handlers
   * that need to do extra UI work (e.g. closing a preceding dialog) should
   * do that work THEN call openGuidedChat — see handleAcceptSpeedWriterInvite
   * below for the canonical pattern.
   */
  const openGuidedChat = () => {
    guidedSeedFiredRef.current = false;
    resetGuidedChat();
    setGuidedOpen(true);
  };

  // Auto-fire the hidden seed prompt every time the modal opens. Runs after
  // React commits resetGuidedChat's state, so `guidedSending` is guaranteed
  // false and no stale-closure race can eat the seed. Ref guard prevents
  // double-fire across re-renders while the modal stays open.
  //
  // The seed text itself lives in backend/config/initial_prayer_prompt.txt
  // (served via GET /prayers/guided-chat/seed) so operators can retune it
  // without a code change. We fall back to a short hardcoded opener if the
  // fetch fails so the modal is never blocked.
  useEffect(() => {
    if (!guidedOpen) {
      guidedSeedFiredRef.current = false;
      return;
    }
    if (guidedSeedFiredRef.current) return;
    if (guidedMessages.length > 0) return;
    if (guidedSending) return;
    guidedSeedFiredRef.current = true;
    const capturedSessionId = guidedSessionId;
    (async () => {
      let seedText = 'Help me create a brief prayer.';
      try {
        const { seed } = await fetchGuidedChatSeed();
        if (seed && typeof seed === 'string' && seed.trim()) {
          seedText = seed.trim();
        }
      } catch (e) {
        console.error('failed to load guided-chat seed prompt', e);
      }
      sendGuidedTurn(
        seedText,
        /* isSeed */ true,
        /* history override */ [],
        /* session override */ capturedSessionId,
      );
    })();
  }, [guidedOpen, guidedSessionId]);

  const sendGuidedTurn = async (text, isSeed = false, historyOverride = null, sessionOverride = null) => {
    const trimmed = (text || '').trim();
    if (!trimmed || guidedSending) return;
    setGuidedError(null);
    // Use the explicit history/session overrides passed by openGuidedChat so
    // the seed request isn't affected by any stale React state that hasn't
    // yet flushed after resetGuidedChat().
    const baseHistory = Array.isArray(historyOverride) ? historyOverride : guidedMessages;
    const nextHistory = [
      ...baseHistory,
      { role: 'user', content: trimmed, hidden: !!isSeed },
    ];
    setGuidedMessages(nextHistory);
    if (!isSeed) setGuidedInput('');
    setGuidedSending(true);
    // Snapshot the current AbortController + session so a mid-flight reset
    // (which rotates both) can't cause us to push a reply into the wrong
    // conversation.
    const requestController = guidedAbortRef.current;
    const requestSessionId = sessionOverride || guidedSessionId;
    try {
      const { reply } = await sendGuidedChat({
        sessionId: requestSessionId,
        message: trimmed,
        // Backend only accepts {role, content}; drop the client-only `hidden` flag.
        history: baseHistory.map(({ role, content }) => ({ role, content })),
        signal: requestController?.signal,
        // Tell the server this is the very first (seed) turn so it enforces a
        // clean [system, hidden_prompt] LLM call and ignores any history.
        isSeed: !!isSeed,
      });
      // If the session was rotated (reset) while this request was in flight,
      // drop the reply on the floor — it belongs to a session that no longer
      // exists on the client.
      if (requestController && requestController.signal.aborted) return;
      if (requestController !== guidedAbortRef.current) return;
      setGuidedMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
    } catch (e) {
      // Cancelled requests are expected during reset — swallow silently.
      if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED' || e?.message === 'canceled') return;
      console.error('guided chat failed', e);
      const msg = e.response?.data?.detail || 'The guide is unavailable right now. Please try again in a moment.';
      setGuidedError(String(msg));
      // Roll back the optimistic user push so the user can retry without duplication.
      setGuidedMessages((prev) => prev.slice(0, -1));
    } finally {
      // Only clear sending if this request still owns the active controller.
      if (requestController === guidedAbortRef.current) {
        setGuidedSending(false);
        // Refocus input for continued conversation
        setTimeout(() => guidedInputRef.current?.focus(), 40);
      }
    }
  };

  // Auto-scroll guided chat to bottom as messages arrive
  useEffect(() => {
    if (!guidedOpen) return;
    const el = guidedScrollRef.current;
    if (el) {
      try { el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' }); }
      catch { el.scrollTop = el.scrollHeight; }
    }
  }, [guidedMessages, guidedOpen, guidedSending]);

  // Strip conversational preface/trailer from a guide reply so we copy only
  // the prayer itself.
  //
  // Defensive cleanup: earlier prompt versions asked the LLM to append a
  // `[[PRAYER_END]]` marker. The current prompt explicitly forbids it,
  // but LLMs occasionally repeat instructions they saw in prior turns —
  // so we still strip the marker if it slips through.
  const PRAYER_END_RE = /\[\[PRAYER_END\]\]/i;
  const stripPrayerEndMarker = (s) => (s || '').replace(/\s*\[\[PRAYER_END\]\]\s*$/i, '').trimEnd();

  // Tradition-appropriate closings the Prayer Assistance may emit as the
  // final line of a prayer draft. Matching any of these on the last line
  // is how we recognise "this chat message contains a prayer" and light
  // up the "Use prayer" button.
  const CLOSING_KEYWORDS = [
    'amen', 'ameen',
    'selah', 'shalom', 'baruch hashem', 'kein yehi ratzon',
    'om shanti', 'om namah shivaya', 'hari om', 'namaste',
    'may all beings', 'sadhu', 'namo buddhaya',
    'waheguru', 'sat sri akal',
    'alláh-u-abhá', 'allah-u-abha', "yá bahá'u'l-abhá", "ya baha'u'l-abha",
    'the tao', 'wu wei', 'so it is',
    'so be it', 'may it be so', 'with gratitude', 'peace',
    "in jesus", 'through christ',
  ];
  const looksLikeClosing = (raw) => {
    const text = stripPrayerEndMarker(raw).trim();
    if (!text) return false;
    const lines = text.split(/\n+/);
    const last = (lines[lines.length - 1] || '').trim().toLowerCase();
    if (!last || last.length > 120) return false;
    return CLOSING_KEYWORDS.some((k) => last.includes(k));
  };

  // 1. Preface: models often prepend "Here's a prayer for you:" or "Of
  //    course, I'd be honored to write this for you:". We drop everything
  //    before the first paragraph that reads like a prayer opening (invocation
  //    word) or the first paragraph after a preface line ending in ":".
  // 2. Trailer: cut everything from the [[PRAYER_END]] marker onward so
  //    the traditional closing (Amen / Ameen / Om Shanti / Waheguru / …)
  //    survives untouched as the final line of the saved prayer body.
  const trimToPrayerBody = (raw) => {
    let text = (raw || '').trim();
    if (!text) return '';

    // ---- 0. Cut at the invisible end-marker ----
    // Everything from `[[PRAYER_END]]` onwards is not part of the prayer
    // (it may include the marker itself + trailing whitespace + any late
    // aside the model tacked on). Trim it out first so downstream
    // preface / trailer logic works against a clean body.
    const markerIdx = text.search(PRAYER_END_RE);
    if (markerIdx !== -1) {
      text = text.slice(0, markerIdx).trim();
    }

    // ---- 1a. Strip the 3rd-person explanation ----
    // The Prayer assistance is instructed to prepend a one-sentence meta note
    // on personal-prayer drafts (e.g. "I've written this in the third
    // person so it reads naturally when others pray it for you.") ahead
    // of a blank line before the actual prayer. That explanation belongs
    // in the CHAT — never in the saved prayer text. Detect any leading
    // paragraph that contains the fingerprint phrase "third person" or
    // "3rd person" and drop it entirely. A real prayer wouldn't contain
    // that meta language, so this fingerprint is safe.
    const THIRD_PERSON_RE = /\b(3rd|third)[- ]person\b/i;
    {
      const parasBefore = text.split(/\n\s*\n/);
      if (parasBefore.length > 1 && THIRD_PERSON_RE.test(parasBefore[0])) {
        text = parasBefore.slice(1).join('\n\n').trim();
      } else if (parasBefore.length === 1) {
        // Same fingerprint can appear as the first LINE followed by the
        // draft on the next line (no blank separator).
        const lines = text.split(/\n/);
        if (lines.length > 1 && THIRD_PERSON_RE.test(lines[0])) {
          text = lines.slice(1).join('\n').trim();
        }
      }
    }

    // ---- 1. Strip preface ----
    const PRAYER_OPENERS = [
      'dear', 'lord', 'god', 'father', 'heavenly', 'holy', 'beloved',
      'blessed', 'almighty', 'loving', 'merciful', 'gracious', 'creator',
      'our father', 'o lord', 'o god', 'o father', 'o holy', 'o divine',
      'great spirit', 'divine', 'mother', 'source of', 'to the',
      'praise be', 'in the name',
    ];
    const isOpener = (para) => {
      const clean = para.trim().toLowerCase();
      if (!clean) return false;
      // Drop leading quotes / markdown emphasis.
      const stripped = clean.replace(/^["'“‘*_>\s]+/, '');
      const firstWord = stripped.split(/[\s,.:;!?]/)[0];
      const firstTwo = stripped.split(/[,.:;!?\n]/)[0].split(/\s+/).slice(0, 2).join(' ');
      return PRAYER_OPENERS.includes(firstWord) || PRAYER_OPENERS.includes(firstTwo);
    };
    const paras = text.split(/\n\s*\n/);
    const firstPrayerIdx = paras.findIndex(isOpener);
    if (firstPrayerIdx > 0) {
      text = paras.slice(firstPrayerIdx).join('\n\n').trim();
    } else if (firstPrayerIdx === -1 && paras.length > 1 && paras[0].trim().endsWith(':')) {
      // Fallback: preface line ends in a colon ("Here's a prayer:") and the
      // next paragraph is the prayer even if we don't recognise its opener.
      text = paras.slice(1).join('\n\n').trim();
    }
    // Also handle single-paragraph prefaces followed by an inline colon+newline
    // (e.g. "Here's a short prayer for you:\nDear Lord, …") when no blank
    // line separator was used.
    const colonNl = text.search(/:\s*\n/);
    if (colonNl !== -1 && colonNl < 200) {
      const rest = text.slice(colonNl + 1).replace(/^\s*/, '');
      if (isOpener(rest)) text = rest;
    }
    // Drop any surrounding quotes the model wrapped the prayer in.
    text = text.replace(/^["'“”‘’]+/, '').replace(/["'“”‘’]+$/, '').trim();

    // ---- 2. Strip trailer ----
    const amenRegex = /\bAmen[.!]?/gi;
    let lastAmen = -1;
    let m;
    while ((m = amenRegex.exec(text)) !== null) {
      lastAmen = m.index + m[0].length;
    }
    if (lastAmen !== -1) return text.slice(0, lastAmen).trim();

    const asideOpeners = [
      'would you like', 'let me know', 'feel free', "if you'd like",
      "if you would like", 'do you want', 'want me to', 'shall i',
      'happy to', 'i can also', 'i can adjust', 'tell me if',
      'would you prefer', "here's ", 'here is ',
    ];
    const lines = text.split(/\n+/);
    const kept = [];
    for (const line of lines) {
      const lower = line.trim().toLowerCase();
      if (asideOpeners.some((o) => lower.startsWith(o))) break;
      kept.push(line);
    }
    return kept.join('\n').trim() || text;
  };

  // Shared perspective analyzer. Fires the same `/prayers/perspective-
  // check` call used by Prayer Assistance so a transcript coming from
  // voice OR video recording gets the automatic 3rd-person alternate
  // generated exactly like a keyboard-typed draft would. Safe to call
  // with an empty/short clean string — it just no-ops in that case.
  //
  // Behaviour matches `applyGuidedReply`:
  //   • Sets `isCheckingPerspective` so the "Preparing shared version…"
  //     status renders under the read-only prayer text.
  //   • On success + 1st-person detection, flips `perspective_type` to
  //     `first_person` and populates `third_person_text` automatically
  //     (no user confirmation modal — matches guided flow).
  //   • Failures are logged but non-fatal — the prayer stays as
  //     third_person so the save flow isn't blocked by an LLM hiccup.
  //   • Updates `lastCheckedDescriptionRef` so the blur-driven re-sync
  //     doesn't re-run OpenAI on the same text moments later.
  const runPerspectiveCheck = async (clean) => {
    const trimmed = (clean || '').trim();
    if (!trimmed) return;
    // Diagnostic: helps confirm this helper actually fires from every
    // entry point (video approve / voice transcribe / guided reply)
    // and shows what the server returned. Left in place while the
    // dual-perspective flow is stabilizing across all four input
    // methods — safe to remove once the video/voice paths are proven
    // in prod.
    console.log('[perspective-check] running with', { title: title.trim(), description: trimmed.slice(0, 60) + '…' });
    setIsCheckingPerspective(true);
    try {
      const res = await checkPerspective({ title: title.trim(), description: trimmed });
      console.log('[perspective-check] response', res);
      if (res && res.is_first_person && (res.third_person_text || '').trim()) {
        setPerspectiveType('first_person');
        setThirdPersonText(res.third_person_text.trim());
      } else {
        // Explicit 3rd-person confirmation from server — clear any
        // stale alternate that might be lingering from a previous
        // draft in the same session.
        setPerspectiveType('third_person');
        setThirdPersonText('');
      }
    } catch (err) {
      console.warn('perspective check failed — leaving as third_person', err);
    } finally {
      setIsCheckingPerspective(false);
      lastCheckedDescriptionRef.current = trimmed;
    }
  };

  // Shared helper: commit a cleaned prayer body into the form. Used by
  // BOTH branches of the perspective decision (3rd-person direct accept
  // AND 1st-person confirmed accept) so title suggestion + focus
  // behaviour stays identical no matter which path the user took.
  const commitPrayerText = (clean, opts = {}) => {
    setDescription(clean);
    setPerspectiveType(opts.perspectiveType || 'third_person');
    setThirdPersonText(opts.thirdPersonText || '');
    setGuidedOpen(false);
    resetGuidedChat();
    if (!title.trim()) {
      suggestPrayerTitle(clean)
        .then(({ title: suggested }) => {
          const t = (suggested || '').trim();
          if (!t) return;
          setTitle((prev) => (prev.trim() ? prev : t.slice(0, 200)));
        })
        .catch((err) => { console.warn('title suggestion failed', err); });
    }
    setTimeout(() => textAreaRef.current?.focus(), 100);
  };

  const applyGuidedReply = async (content) => {
    const clean = trimToPrayerBody(content).slice(0, 2000);
    if (!clean) return;
    // Populate the form IMMEDIATELY with the clean text so the user
    // sees their prayer land in the description field the moment they
    // tap "Use prayer". Perspective is provisionally set to
    // "third_person" until the check comes back — this way the flow
    // never blocks on the LLM call and the loading indicator can
    // render inside the form (not the chat modal).
    commitPrayerText(clean, { perspectiveType: 'third_person', thirdPersonText: '' });
    setCreationMethod('assistance');
    // Kick off the perspective check in the background. If it detects
    // 1st person, flip `perspective_type` + populate `third_person_text`
    // AUTOMATICALLY (no confirmation). While it's in flight, the form
    // shows a "Preparing shared version…" status message under the
    // read-only prayer text.
    await runPerspectiveCheck(clean);
  };

  // Blur-driven re-sync of the dual-perspective pair. Fires when the
  // user has manually edited the Prayer Text field AND a 3rd-person
  // alternate version already exists (i.e., Prayer Assistance previously
  // drafted the prayer in the 1st person and auto-generated the 3rd-
  // person twin). Runs in three shapes:
  //   • Fresh manually-typed prayer (no existing 3rd-person twin) →
  //     first-time perspective check + generate 3rd-person version if
  //     the text is written in the 1st person. Parity with the voice
  //     and video transcription paths, which both call
  //     `runPerspectiveCheck` immediately after the transcript lands.
  //   • Edited prayer that already has a 1st-person + 3rd-person pair
  //     → re-sync the 3rd-person twin so it stays aligned with the
  //     user's edits.
  //   • Rewritten directly in the 3rd person → drop the alternate
  //     entirely (`third_person_text = ''`, `perspective_type =
  //     'third_person'`).
  // No-ops when the text hasn't changed since the last analysis, when
  // the field is empty, or when a check is already in flight.
  const handleDescriptionBlur = async () => {
    const current = description.trim();
    if (!current) return;
    if (isCheckingPerspective) return;
    if (current === (lastCheckedDescriptionRef.current || '').trim()) return;
    setIsCheckingPerspective(true);
    try {
      const res = await checkPerspective({ title: title.trim(), description: current });
      if (res && res.is_first_person && (res.third_person_text || '').trim()) {
        // 1st person → set/refresh the 3rd-person twin.
        setPerspectiveType('first_person');
        setThirdPersonText(res.third_person_text.trim());
      } else {
        // 3rd person (or unclassifiable) → drop any stale alternate.
        setPerspectiveType('third_person');
        setThirdPersonText('');
      }
    } catch (err) {
      console.warn('perspective check on blur failed — keeping previous state', err);
    } finally {
      setIsCheckingPerspective(false);
      lastCheckedDescriptionRef.current = current;
    }
    // Auto-title suggestion for MANUALLY-TYPED prayer text. Matches
    // the same helper called by the voice / video / guided flows so
    // all four input methods converge on identical title-suggestion
    // behaviour. Only runs when the Title field is still empty — never
    // overwrites the user's own words. Fires in the background AFTER
    // the perspective check finishes so both state updates land in
    // sequence without racing each other.
    if (!title.trim()) {
      suggestPrayerTitle(current)
        .then(({ title: suggested }) => {
          const t = (suggested || '').trim();
          if (!t) return;
          setTitle((prev) => (prev.trim() ? prev : t.slice(0, 200)));
        })
        .catch((err) => { console.warn('manual-text title suggestion failed', err); });
    }
  };


  const canSubmit = title.trim() && description.trim() && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    // Safety-net perspective check — a user who types their prayer and
    // hits Publish without ever blurring the textarea would otherwise
    // ship with no 3rd-person twin. This runs `runPerspectiveCheck`
    // one final time on the current description IF the text differs
    // from whatever was last analysed. AWAITED so the resulting
    // `perspective_type` + `third_person_text` state is committed
    // before the submit payload is built below.
    try {
      const current = description.trim();
      if (current && current !== (lastCheckedDescriptionRef.current || '').trim() && !isCheckingPerspective) {
        await runPerspectiveCheck(current);
      }
    } catch (err) {
      // Never block submit on a perspective check failure — the server
      // will proceed with whatever pair we already have.
      console.warn('pre-submit perspective check failed', err);
    }
    try {
      if (isEditing) {
        // Edit path: PATCH the existing prayer with the editable fields.
        const updates = {
          title: title.trim(),
          description: description.trim(),
          visibility,
          hashtags,
          is_silent: isSilent,
          // Perspective — always send the current form state so a
          // re-engagement of Prayer Assistance on the edit page actually
          // persists. When 1st person: sends the alternate too. When
          // 3rd person: `third_person_text` is cleared server-side.
          perspective_type: (perspectiveType === 'first_person' && thirdPersonText.trim())
            ? 'first_person'
            : 'third_person',
          third_person_text: (perspectiveType === 'first_person' && thirdPersonText.trim())
            ? thirdPersonText.trim()
            : null,
          // Send background color intent explicitly — null clears any
          // previously chosen color and returns the prayer to the app default.
          background_color: (noImage && backgroundColor) ? backgroundColor : null,
          // Video URL — sent whenever the user has recorded a clip
          // during this edit session. Backend accepts null to detach
          // an existing video (e.g. if the user re-records with
          // voice-only). We only include this key when we actually
          // have a value so we don't accidentally clear an existing
          // clip on an unrelated PATCH.
          ...(videoUrl ? { video_url: videoUrl } : {}),
        };
        // If the user explicitly chose "No image" on a prayer that has one,
        // instruct the backend to clear it.
        if (noImage && existingImageUrl) {
          updates.remove_image = true;
        }
        // If the user picked a NEW cover image during edit, upload it via
        // the dedicated image-replace endpoint first (JSON PATCH can't
        // carry file bytes). Any Sanctuary-Moderation block on the image
        // aborts the whole save so the text updates don't slip through.
        if (imageFile) {
          await replacePrayerImage(editingId, imageFile);
        }
        await updatePrayer(editingId, updates);
        navigate(`/prayer/${editingId}`, { replace: true });
        return;
      }

      // New-prayer flow: POST fresh with any attached media.
      const form = new FormData();
      form.append('title', title.trim());
      form.append('description', description.trim());
      form.append('is_silent', isSilent ? 'true' : 'false');
      if (shareRandom) form.append('share_random', 'true');
      // Perspective — only send when we actually captured a 1st-person
      // draft alongside its 3rd-person alternate. Otherwise the backend
      // default kicks in (third_person, no alternate).
      if (perspectiveType === 'first_person' && thirdPersonText.trim()) {
        form.append('perspective_type', 'first_person');
        form.append('third_person_text', thirdPersonText.trim());
      }
      if (imageFile) form.append('image', imageFile);
      form.append('visibility', visibility);
      if (hashtags.length) {
        form.append('hashtags', hashtags.map(t => `#${t}`).join(' '));
      }
      if (noImage && backgroundColor) {
        form.append('background_color', backgroundColor);
      }
      // Attach the Cloudinary video URL when the user recorded a clip
      // via the video method. Backend persists it on the prayer doc
      // and returns it in the Watch page payload.
      if (videoUrl) {
        form.append('video_url', videoUrl);
      }
      // Creation method — persisted so Manage Prayers can show
      // Video / Audio / Text / Assistance in the Type column without
      // inferring from `video_url` / `audio_url`.
      form.append('creation_method', creationMethod);
      if (audioBlob) {
        form.append('audio', audioBlob, `prayer-${Date.now()}.webm`);
        form.append('duration_sec', String(audioDuration || 0));
      } else {
        form.append('duration_sec', '0');
      }
      const { data } = await api.post('/prayers', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      navigate(`/prayer/${data.id}`, { replace: true });
    } catch (e) {
      console.error(e);
      // Sanctuary Moderation blocks: surface via the red modal instead of
      // the inline error banner. If it's not a moderation error, fall
      // back to the standard user-friendly error text below.
      const sanct = parseSanctuaryError(e);
      if (sanct) {
        setSanctuary(sanct);
      } else {
        const status = e.response?.status;
        const detail = e.response?.data?.detail;
        let msg;
        if (detail) msg = detail;
        else if (status === 401) msg = 'Your session expired. Please sign in again.';
        else if (status === 413) msg = 'Your image or audio is too large.';
        else if (status >= 502 && status <= 504) msg = 'Storage service is temporarily unavailable. Please try again.';
        else msg = e.message || (isEditing ? 'Save failed. Please try again.' : 'Publish failed. Please try again.');
        setError(String(msg));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- Section helpers ----------
  const titleDone = title.trim().length > 0;
  const prayerDone = Boolean(description.trim());

  if (loading || loadingExisting) return <div className="p-10 text-center text-zinc-500">Loading…</div>;
  if (loadError) return <div className="p-10 text-center text-zinc-500">{loadError}</div>;
  if (!user) {
    return (
      <div className="p-10 text-center">
        <p className="text-zinc-600 dark:text-zinc-400 mb-4">Please sign in to share a prayer.</p>
        <button
          onClick={login}
          data-testid="signin-btn"
          className="h-9 px-4 rounded-full bg-zinc-900 text-white dark:bg-white dark:text-zinc-900 text-sm font-medium"
        >
          Sign in
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6 pnt-suppress-focus-ring" data-testid="add-prayer-page">
      <SanctuaryModerationDialog
        open={Boolean(sanctuary)}
        variant={sanctuary?.variant}
        reason={sanctuary?.reason}
        source={sanctuary?.source}
        onClose={() => setSanctuary(null)}
      />

      <h1 className="text-2xl font-bold mb-6">{isEditing ? 'Edit your prayer' : 'Create your prayer'}</h1>

      {/* Section 1 — Prayer Text (moved above Title per product decision:
          most authors have the prayer content in mind first, and land
          on a title after they've written the body — often auto-
          suggested by the LLM helper on voice/video transcription. */}
      <Section
        number={1}
        title="Prayer Text (required)"
        isOpen={isSectionOpen(SECTIONS.PRAYER)}
        onToggle={() => toggleSection(SECTIONS.PRAYER)}
        done={prayerDone}
        testId="section-prayer"
      >
        {/* Icon toolbar — three tap targets sitting directly above the
            prayer textarea:
              • Microphone → opens the recorder modal so the user can
                dictate their prayer and have it transcribed.
              • Keyboard   → focuses the textarea for immediate typing.
              • Assistance → opens the guided Prayer Assistance chat
                (was previously called "Re-engage Prayer Assistance").
            The trio replaces the older single "Re-engage" button so all
            three input modes are equally discoverable. */}
        <div className="flex items-center gap-2 mb-3" data-testid="prayer-input-toolbar">
          <button
            type="button"
            onClick={openRecorder}
            data-testid="prayer-input-mic-btn"
            aria-label="Record your voice"
            title="Record your voice"
            className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-200 hover:bg-amber-50 hover:text-amber-700 hover:border-amber-400 dark:hover:bg-amber-950/40 dark:hover:text-amber-300 active:scale-95 transition"
          >
            <Mic size={16} />
          </button>
          <button
            type="button"
            onClick={openVideoRecorder}
            data-testid="prayer-input-video-btn"
            aria-label="Record video"
            title="Record a video (max 1 minute)"
            className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-200 hover:bg-amber-50 hover:text-amber-700 hover:border-amber-400 dark:hover:bg-amber-950/40 dark:hover:text-amber-300 active:scale-95 transition"
          >
            <Video size={16} />
          </button>
          <button
            type="button"
            onClick={() => { textAreaRef.current?.focus(); }}
            data-testid="prayer-input-keyboard-btn"
            aria-label="Type your prayer"
            title="Type your prayer"
            className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-200 hover:bg-amber-50 hover:text-amber-700 hover:border-amber-400 dark:hover:bg-amber-950/40 dark:hover:text-amber-300 active:scale-95 transition"
          >
            <Keyboard size={16} />
          </button>
          <button
            type="button"
            onClick={openGuidedChat}
            data-testid="reengage-guide-btn"
            aria-label={isEditing ? 'Re-engage Prayer Assistance' : 'Open Prayer Assistance'}
            title={isEditing ? 'Re-engage Prayer Assistance' : 'Open Prayer Assistance'}
            className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-200 hover:bg-amber-50 hover:text-amber-700 hover:border-amber-400 dark:hover:bg-amber-950/40 dark:hover:text-amber-300 active:scale-95 transition"
          >
            <Zap size={16} />
          </button>
          <span className="text-[11px] text-zinc-500 ml-1">
            Record, type, or draft with Assistance.
          </span>
        </div>
        {/* Textarea lock — prevent mismatches between the audio track
            and the on-screen text:
              1. While a recording is actively in progress, the text is
                 read-only so the user can focus on speaking.
              2. When the "Use my voice recording for narration" toggle
                 is set to Yes (and a recording exists), the text stays
                 in lockstep with the transcription — editing would
                 make the audio out of sync with what viewers read.
                 Switching the toggle to No re-unlocks the field. */}
        {(() => {
          const _baseline = (recordingTextSnapshot ?? '').trim();
          const _current = (description || '').trim();
          const _hasRecording = Boolean(audioBlob || existingAudioUrl);
          const textareaLocked =
            recording ||
            (_hasRecording && !isSilent && _baseline === _current);
          return (
        <textarea
          ref={textAreaRef}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onBlur={handleDescriptionBlur}
          maxLength={2000}
          rows={8}
          readOnly={textareaLocked}
          data-testid="prayer-description-input"
          data-textarea-locked={textareaLocked ? 'true' : 'false'}
          placeholder={
            recording
              ? 'Recording in progress — text will be transcribed when you stop.'
              : (textareaLocked
                  ? 'Locked while “Use my voice recording” is Yes. Switch to No to edit.'
                  : 'Enter text here')
          }
          className={
            'w-full px-3 py-3 rounded-lg border text-sm resize-none focus:outline-none focus:ring-2 focus:ring-amber-400/60 leading-relaxed ' +
            (textareaLocked
              ? 'bg-zinc-100 dark:bg-zinc-800/60 border-zinc-300 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 cursor-not-allowed'
              : 'bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-700 text-zinc-800 dark:text-zinc-100')
          }
        />
          );
        })()}
        <div className="mt-1 flex items-center justify-end gap-3 text-[11px] text-zinc-400">
          <span className="tabular-nums">{description.length}/2000</span>
        </div>

        {/* Status message — surfaced while the perspective check + 3rd-
            person generation is in flight, so the user knows another
            piece of the flow is still running after the primary text
            lands in the description field. Positioned in the same slot
            as the 3rd-person alternate card so the two never appear at
            the same time. */}
        {isCheckingPerspective && (
          <div
            className="mt-4 p-3 rounded-lg border border-amber-200/70 dark:border-amber-900/60 bg-amber-50/40 dark:bg-amber-950/20 flex items-center gap-2"
            data-testid="perspective-checking-status"
            role="status"
            aria-live="polite"
          >
            <Loader2 size={14} className="animate-spin text-amber-600 dark:text-amber-400" />
            <span className="text-xs text-amber-700 dark:text-amber-300 font-medium">
              Creating 3rd person version…
            </span>
          </div>
        )}

        {/* 3rd-person alternate — only rendered when the author accepted a
            1st-person draft AND we have the auto-generated 3rd-person
            rewrite in state. Shown below the read-only prayer text as a
            reference so the author knows what other viewers will read
            when they open the prayer on the watch page. */}
        {perspectiveType === 'first_person' && thirdPersonText.trim() && (
          <div
            className="mt-4 p-3 rounded-lg border border-amber-200/70 dark:border-amber-900/60 bg-amber-50/60 dark:bg-amber-950/20"
            data-testid="third-person-alternate"
          >
            <div className="text-[11px] uppercase tracking-wider font-semibold text-amber-700 dark:text-amber-300 mb-1.5">
              3rd-person version <span className="text-amber-600/70 dark:text-amber-400/70 normal-case tracking-normal font-normal">— shown to other viewers</span>
            </div>
            <div className="text-sm text-zinc-800 dark:text-zinc-200 whitespace-pre-wrap leading-relaxed" data-testid="third-person-alternate-text">
              {thirdPersonText}
            </div>
          </div>
        )}

        {/* Voice-narration selector — only shown when the owner has actually
            recorded (or previously uploaded) their own voice. Controls
            whether that recording is used for viewer narration. When set to
            "No", the recording stays on the prayer but is treated as
            unavailable during playback (viewers fall back to their own
            narration preference). Default: Yes. Persisted server-side as
            the inverse `is_silent` flag. */}
        {(audioBlob || existingAudioUrl) && (() => {
          // If the user is currently on "No" and has edited the text so it
          // no longer matches the recording's transcription, we lock out
          // the "Yes" option — a stale recording can't be paired with new
          // text. Baseline is captured whenever a fresh recording is made
          // (or on load in edit mode). A re-record resets it.
          const baseline = (recordingTextSnapshot ?? '').trim();
          const current = (description || '').trim();
          const textDiffersFromRecording = baseline !== current;
          const yesDisabled = isSilent && textDiffersFromRecording;
          return (
          <div
            className="mt-3 flex items-center justify-between gap-3 rounded-lg border border-zinc-200 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-900/40 px-3 py-2.5"
            data-testid="silent-prayer-toggle-row"
          >
            <div className="min-w-0">
              <div className="text-sm font-medium text-zinc-800 dark:text-zinc-200">Use my voice recording for narration</div>
              <div className="text-[11px] text-zinc-500 dark:text-zinc-400 leading-snug mt-0.5">
                {yesDisabled
                  ? 'You edited the transcription — re-record to use your voice for narration again.'
                  : 'When off, viewers hear their own narration preference instead of your recording.'}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0" role="radiogroup" aria-label="Use my voice recording for narration">
              <button
                type="button"
                role="radio"
                aria-checked={!isSilent}
                aria-disabled={yesDisabled}
                disabled={yesDisabled}
                data-testid="silent-prayer-off-btn"
                data-yes-disabled={yesDisabled ? 'true' : 'false'}
                onClick={() => { if (!yesDisabled) setIsSilent(false); }}
                title={yesDisabled ? 'Transcription has been edited — re-record to re-enable' : undefined}
                className={
                  'px-3 h-8 rounded-full text-xs font-medium border transition-colors ' +
                  (yesDisabled
                    ? 'bg-transparent border-zinc-200 dark:border-zinc-800 text-zinc-300 dark:text-zinc-600 cursor-not-allowed'
                    : (!isSilent
                      ? 'bg-amber-500 border-amber-500 text-white shadow-sm'
                      : 'bg-transparent border-zinc-300 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800'))
                }
              >
                Yes
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={isSilent}
                data-testid="silent-prayer-on-btn"
                onClick={() => setIsSilent(true)}
                className={
                  'px-3 h-8 rounded-full text-xs font-medium border transition-colors ' +
                  (isSilent
                    ? 'bg-amber-500 border-amber-500 text-white shadow-sm'
                    : 'bg-transparent border-zinc-300 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800')
                }
              >
                No
              </button>
            </div>
          </div>
          );
        })()}
        {audioBlob ? (
          <div className="mt-3 flex items-center gap-2 rounded-lg border border-zinc-200 dark:border-zinc-800 p-2 bg-zinc-50 dark:bg-zinc-900" data-testid="saved-audio-strip">
            <audio ref={audioPlaybackRef} src={audioPlaybackUrl || undefined} onEnded={() => setAudioPlaying(false)} className="hidden" />
            <button
              type="button"
              onClick={toggleAudioPlayback}
              data-testid="saved-audio-play"
              className="w-9 h-9 rounded-full bg-zinc-900 text-white dark:bg-white dark:text-zinc-900 flex items-center justify-center"
            >
              {audioPlaying ? <Pause size={14} /> : <Play size={14} />}
            </button>
            <div className="text-xs flex-1">
              <div className="font-medium">Voice recording saved</div>
              <div className="text-zinc-500 tabular-nums">{fmtTime(audioDuration)}</div>
            </div>
            <button
              type="button"
              onClick={removeSavedAudio}
              data-testid="remove-saved-audio"
              aria-label="Discard voice recording"
              className="p-2 rounded-full hover:bg-zinc-200 dark:hover:bg-zinc-800 text-zinc-500"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ) : existingAudioUrl && (
          <div className="mt-3 flex items-center gap-2 rounded-lg border border-zinc-200 dark:border-zinc-800 p-2 bg-zinc-50 dark:bg-zinc-900" data-testid="existing-audio-strip">
            <audio controls src={existingAudioUrl} className="flex-1 h-9" preload="none" />
            <div className="text-xs text-zinc-500 whitespace-nowrap hidden sm:block">
              Tap the mic above to re-record
            </div>
          </div>
        )}
      </Section>

      {/* Section 2 — Title (below Prayer Text so the auto-suggested
          title from a voice/video transcript lands in this field
          right after the body appears above). */}
      <Section
        number={2}
        title="Title (required)"
        isOpen={isSectionOpen(SECTIONS.TITLE)}
        onToggle={() => toggleSection(SECTIONS.TITLE)}
        done={titleDone}
        testId="section-title"
      >
        <label className="text-xs text-zinc-500 block mb-2">A short title for your prayer <span className="text-red-500">*</span></label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={200}
          data-testid="prayer-title-input"
          placeholder="Enter text here"
          className="w-full h-11 px-3 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-800 dark:text-zinc-100 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/60"
        />
      </Section>

      {/* Section 3 — Cover image */}
      <Section
        number={3}
        title="Cover image"
        isOpen={isSectionOpen(SECTIONS.COVER)}
        onToggle={() => toggleSection(SECTIONS.COVER)}
        done={Boolean(imageFile)}
        testId="section-cover"
      >
        <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 p-4 bg-zinc-50 dark:bg-zinc-900">
          {noImage ? (
            <div className="flex flex-col gap-4" data-testid="no-image-preview">
              <div className="flex items-start gap-4">
                <div
                  className="w-24 h-24 rounded-lg overflow-hidden ring-1 ring-black/5 dark:ring-white/10"
                  style={{ background: bgGradient(backgroundColor) }}
                  data-testid="no-image-swatch-preview"
                  aria-hidden="true"
                />
                <div className="flex-1 flex flex-col gap-2">
                  <div className="text-sm font-medium">No cover image</div>
                  <div className="text-xs text-zinc-500">
                    {backgroundColor
                      ? `Your prayer will use ${backgroundColor.toUpperCase()} as its background.`
                      : 'Your prayer will use a soft purple gradient as its background.'}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-1">
                    <button onClick={() => { clearNoImage(); cameraInputRef.current?.click(); }} data-testid="pick-camera-from-none" className="text-sm px-3 py-1.5 rounded-md bg-zinc-200 dark:bg-zinc-800 hover:bg-zinc-300 dark:hover:bg-zinc-700 flex items-center gap-2">
                      <Camera size={14} /> Take photo
                    </button>
                    <button onClick={() => { clearNoImage(); galleryInputRef.current?.click(); }} data-testid="pick-upload-from-none" className="text-sm px-3 py-1.5 rounded-md bg-zinc-200 dark:bg-zinc-800 hover:bg-zinc-300 dark:hover:bg-zinc-700 flex items-center gap-2">
                      <ImageIcon size={14} /> Upload image
                    </button>
                    <button onClick={() => { clearNoImage(); openGalleryDialog(); }} data-testid="pick-gallery-from-none" className="text-sm px-3 py-1.5 rounded-md bg-zinc-200 dark:bg-zinc-800 hover:bg-zinc-300 dark:hover:bg-zinc-700 flex items-center gap-2">
                      <Sunrise size={14} /> Gallery
                    </button>
                  </div>
                </div>
              </div>

              {/* Background color picker — appears once "No image" is chosen */}
              <div className="pt-3 border-t border-zinc-200 dark:border-zinc-800" data-testid="bg-color-picker">
                <div className="text-xs font-medium text-zinc-700 dark:text-zinc-300 mb-2">Choose a background color</div>
                <div className="flex flex-wrap items-center gap-2">
                  {/* Default gradient chip */}
                  <button
                    type="button"
                    onClick={() => setBackgroundColor(null)}
                    data-testid="bg-swatch-gradient"
                    aria-label="Purple gradient (default)"
                    title="Purple gradient (default)"
                    className={`w-9 h-9 rounded-full ring-2 transition-transform hover:scale-110 ${
                      backgroundColor === null ? 'ring-amber-500 scale-110' : 'ring-transparent'
                    }`}
                    style={{ background: 'linear-gradient(to bottom, #2e1065, #7c3aed, #c4b5fd)' }}
                  />
                  {[
                    '#0f172a', // slate 900 (deep night)
                    '#1e3a8a', // blue 900
                    '#065f46', // emerald 800
                    '#78350f', // amber 900 (warm brown)
                    '#9f1239', // rose 800
                    '#7c2d12', // orange 900
                    '#374151', // gray 700
                    '#f3f4f6', // gray 100 (light)
                    '#fef3c7', // amber 100 (soft cream)
                    '#fce7f3', // pink 100 (soft blush)
                    '#dbeafe', // blue 100 (sky)
                    '#dcfce7', // green 100 (mint)
                  ].map((hex) => (
                    <button
                      key={hex}
                      type="button"
                      onClick={() => setBackgroundColor(hex)}
                      data-testid={`bg-swatch-${hex.replace('#', '')}`}
                      aria-label={`Background ${hex}`}
                      title={hex.toUpperCase()}
                      className={`w-9 h-9 rounded-full ring-2 transition-transform hover:scale-110 ${
                        backgroundColor === hex ? 'ring-amber-500 scale-110' : 'ring-transparent'
                      }`}
                      style={{ backgroundColor: hex }}
                    />
                  ))}
                  {/* Custom color input — native picker */}
                  <label
                    className="inline-flex items-center gap-1.5 h-9 pl-1 pr-3 rounded-full bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-xs font-medium cursor-pointer"
                    data-testid="bg-swatch-custom"
                    title="Pick any color"
                  >
                    <span
                      className="w-6 h-6 rounded-full ring-1 ring-zinc-300 dark:ring-zinc-600"
                      style={{
                        background: backgroundColor && !['#0f172a','#1e3a8a','#065f46','#78350f','#9f1239','#7c2d12','#374151','#f3f4f6','#fef3c7','#fce7f3','#dbeafe','#dcfce7'].includes(backgroundColor)
                          ? backgroundColor
                          : 'conic-gradient(from 0deg, #ef4444, #eab308, #22c55e, #06b6d4, #6366f1, #d946ef, #ef4444)',
                      }}
                    />
                    <span>Custom</span>
                    <input
                      type="color"
                      value={backgroundColor && /^#[0-9a-fA-F]{6}$/.test(backgroundColor) ? backgroundColor : '#7c3aed'}
                      onChange={(e) => setBackgroundColor(e.target.value)}
                      className="sr-only"
                      data-testid="bg-color-input"
                    />
                  </label>
                </div>
              </div>
            </div>
          ) : imagePreview ? (
            <div className="flex items-start gap-4">
              <img src={imagePreview} alt="cover preview" className="w-24 h-24 object-cover rounded-lg" />
              <div className="flex-1 flex flex-col gap-2">
                <button onClick={() => cameraInputRef.current?.click()} className="text-sm px-3 py-1.5 rounded-md bg-zinc-200 dark:bg-zinc-800 hover:bg-zinc-300 dark:hover:bg-zinc-700 self-start flex items-center gap-2">
                  <Camera size={14} /> Retake with camera
                </button>
                <button onClick={() => galleryInputRef.current?.click()} className="text-sm px-3 py-1.5 rounded-md bg-zinc-200 dark:bg-zinc-800 hover:bg-zinc-300 dark:hover:bg-zinc-700 self-start flex items-center gap-2">
                  <ImageIcon size={14} /> Upload a different image
                </button>
                <button onClick={openGalleryDialog} data-testid="open-sunrise-gallery-btn-alt" className="text-sm px-3 py-1.5 rounded-md bg-zinc-200 dark:bg-zinc-800 hover:bg-zinc-300 dark:hover:bg-zinc-700 self-start flex items-center gap-2">
                  <Sunrise size={14} /> Pick from Gallery
                </button>
                <button onClick={chooseNoImage} data-testid="no-image-from-preview-btn" className="text-sm px-3 py-1.5 rounded-md bg-zinc-200 dark:bg-zinc-800 hover:bg-zinc-300 dark:hover:bg-zinc-700 self-start flex items-center gap-2">
                  <X size={14} /> No image - color only
                </button>
                <button onClick={removeImage} data-testid="remove-image-btn" className="text-xs px-3 py-1 rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40 self-start flex items-center gap-2">
                  <Trash2 size={12} /> Remove
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-2">
              <button onClick={() => cameraInputRef.current?.click()} data-testid="camera-btn" className="flex-1 h-11 rounded-lg border border-dashed border-zinc-300 dark:border-zinc-700 flex items-center justify-center gap-2 text-sm hover:bg-zinc-100 dark:hover:bg-zinc-800">
                <Camera size={16} /> Take photo
              </button>
              <button onClick={() => galleryInputRef.current?.click()} data-testid="gallery-btn" className="flex-1 h-11 rounded-lg border border-dashed border-zinc-300 dark:border-zinc-700 flex items-center justify-center gap-2 text-sm hover:bg-zinc-100 dark:hover:bg-zinc-800">
                <ImageIcon size={16} /> Upload image
              </button>
              <button onClick={openGalleryDialog} data-testid="open-sunrise-gallery-btn" className="flex-1 h-11 rounded-lg border border-dashed border-zinc-300 dark:border-zinc-700 flex items-center justify-center gap-2 text-sm hover:bg-zinc-100 dark:hover:bg-zinc-800">
                <Sunrise size={16} /> Gallery
              </button>
              <button onClick={chooseNoImage} data-testid="no-image-btn" className="flex-1 h-11 rounded-lg border border-dashed border-zinc-300 dark:border-zinc-700 flex items-center justify-center gap-2 text-sm hover:bg-zinc-100 dark:hover:bg-zinc-800">
                <X size={16} /> No image - color only
              </button>
            </div>
          )}
          <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleImage} className="hidden" data-testid="camera-input" />
          <input ref={galleryInputRef} type="file" accept="image/*" onChange={handleImage} className="hidden" data-testid="gallery-input" />
        </div>
      </Section>

      {/* Section 4 — Share options (privacy) */}
      <Section
        number={4}
        title="Share options"
        isOpen={isSectionOpen(SECTIONS.SHARE)}
        onToggle={() => toggleSection(SECTIONS.SHARE)}
        done={true}
        testId="section-share"
      >
        <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900 divide-y divide-zinc-200 dark:divide-zinc-800" role="radiogroup" aria-label="Prayer privacy">
          <label
            data-testid="visibility-public-row"
            className={`flex items-start gap-3 p-3 cursor-pointer ${visibility === 'public' ? 'bg-amber-50 dark:bg-amber-950/30' : ''}`}
          >
            <input
              type="radio"
              name="visibility"
              value="public"
              checked={visibility === 'public'}
              onChange={() => setVisibility('public')}
              data-testid="visibility-public-input"
              className="mt-1 accent-amber-500"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Globe size={14} className="text-amber-600" /> Public
              </div>
              <div className="text-xs text-zinc-500 mt-0.5">
                Accessible to everyone.
              </div>
            </div>
          </label>
          <label
            data-testid="visibility-circle-row"
            className={`flex items-start gap-3 p-3 cursor-pointer ${visibility === 'circle' ? 'bg-amber-50 dark:bg-amber-950/30' : ''}`}
          >
            <input
              type="radio"
              name="visibility"
              value="circle"
              checked={visibility === 'circle'}
              onChange={() => setVisibility('circle')}
              data-testid="visibility-circle-input"
              className="mt-1 accent-amber-500"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Users size={14} className="text-amber-600" /> Unlisted — but shown on my circle
                {/* Default badge — the small pill signals to authors
                    that this is the pre-selected privacy tier, so a
                    quick "Publish" without touching the section still
                    lands them in the conservative-privacy bucket
                    (not on public feeds). */}
                <span className="ml-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-400">
                  Default
                </span>
              </div>
              <div className="text-xs text-zinc-500 mt-0.5">
                Not shown on Home, Nearby, Random or Search. Visible on your Circle Page and to anyone with the direct link.
              </div>
            </div>
          </label>
          <label
            data-testid="visibility-unlisted-row"
            className={`flex items-start gap-3 p-3 cursor-pointer ${visibility === 'unlisted' ? 'bg-amber-50 dark:bg-amber-950/30' : ''}`}
          >
            <input
              type="radio"
              name="visibility"
              value="unlisted"
              checked={visibility === 'unlisted'}
              onChange={() => setVisibility('unlisted')}
              data-testid="visibility-unlisted-input"
              className="mt-1 accent-amber-500"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Link2 size={14} className="text-amber-600" /> Unlisted
              </div>
              <div className="text-xs text-zinc-500 mt-0.5">
                Not shown on the home page. Only people with a direct link to the prayer can view.
              </div>
            </div>
          </label>
        </div>

        {/* Share with a random member — an ADDITIONAL, independent
            option layered on top of the Public/Unlisted choice above.
            It is NOT a third visibility mode — a Public prayer stays
            public (visible on home) and an Unlisted prayer stays
            unlisted (link only) regardless of this toggle. Enabling it
            simply fan-outs a direct notification to one random member on
            creation. Not offered on edit — random fan-out only fires at
            prayer birth. */}
        {/* Hashtags — visible field HIDDEN to keep the compose form
            focused on the prayer itself. The state, the AI
            auto-suggest effect, and the form submission field are
            all preserved so tags still land on the prayer document
            for analytics (trending, discovery, moderation heuristics).
            Flip `SHOW_HASHTAG_UI` back to `true` to re-expose. */}
        {SHOW_HASHTAG_UI && (
        <div className="mt-4" data-testid="hashtags-section">
          <div className="flex items-center justify-between mb-1.5 gap-2 flex-wrap">
            <label htmlFor="hashtag-input" className="block text-sm font-medium">
              Hashtags <span className="text-xs text-zinc-500 font-normal">(optional)</span>
            </label>
            <button
              type="button"
              onClick={() => runAiHashtagSuggest('manual')}
              disabled={aiHashtagsBusy || !description.trim() || hashtags.length >= 10}
              data-testid="hashtag-ai-suggest-btn"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-600 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              title="Ask AI to suggest tags based on your prayer text"
            >
              <Zap size={13} strokeWidth={2.5} />
              {aiHashtagsBusy ? 'Suggesting…' : 'Suggest with AI'}
            </button>
          </div>
          <p className="text-xs text-zinc-500 mb-2">
            Add up to 10 tags to help people discover your prayer. Press Enter, comma, or space to add.
          </p>
          {aiHashtagsError && (
            <p
              className="text-xs text-amber-700 dark:text-amber-400 mb-2"
              data-testid="hashtag-ai-error"
            >
              {aiHashtagsError}
            </p>
          )}
          <div
            className="flex flex-wrap items-center gap-2 min-h-[44px] p-2 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-950 focus-within:ring-2 focus-within:ring-amber-400"
            onClick={(e) => {
              // Focus the inner input if the container is clicked directly.
              if (e.target === e.currentTarget) {
                e.currentTarget.querySelector('input')?.focus();
              }
            }}
          >
            {hashtags.map((t) => (
              <span
                key={t}
                data-testid={`hashtag-chip-${t}`}
                className="inline-flex items-center gap-1 h-7 pl-2.5 pr-1 rounded-full bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 text-xs font-medium"
              >
                #{t}
                <button
                  type="button"
                  onClick={() => setHashtags(hashtags.filter(h => h !== t))}
                  data-testid={`hashtag-remove-${t}`}
                  aria-label={`Remove hashtag ${t}`}
                  className="w-5 h-5 rounded-full inline-flex items-center justify-center hover:bg-amber-200 dark:hover:bg-amber-900/50"
                >
                  <X size={12} />
                </button>
              </span>
            ))}
            <input
              id="hashtag-input"
              data-testid="hashtag-input"
              type="text"
              value={hashtagInput}
              onChange={(e) => {
                const v = e.target.value;
                // If the user typed a separator (space/comma/enter=handled onKeyDown),
                // commit any completed tag(s) automatically.
                if (/[\s,]/.test(v)) {
                  const parts = v.split(/[\s,]+/);
                  const trailing = parts.pop() || '';
                  const nextTags = [...hashtags];
                  for (const raw of parts) {
                    const clean = raw.replace(/^#+/, '').toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 32);
                    if (clean && !nextTags.includes(clean) && nextTags.length < 10) {
                      nextTags.push(clean);
                    }
                  }
                  setHashtags(nextTags);
                  setHashtagInput(trailing);
                } else {
                  setHashtagInput(v);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
                  e.preventDefault();
                  const raw = hashtagInput.replace(/^#+/, '').toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 32);
                  if (raw && !hashtags.includes(raw) && hashtags.length < 10) {
                    setHashtags([...hashtags, raw]);
                  }
                  setHashtagInput('');
                } else if (e.key === 'Backspace' && hashtagInput === '' && hashtags.length > 0) {
                  // Backspace on empty input removes the last chip (common tag-input UX).
                  setHashtags(hashtags.slice(0, -1));
                }
              }}
              onBlur={() => {
                // Commit any pending value on blur so tags aren't lost.
                const raw = hashtagInput.replace(/^#+/, '').toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 32);
                if (raw && !hashtags.includes(raw) && hashtags.length < 10) {
                  setHashtags([...hashtags, raw]);
                }
                setHashtagInput('');
              }}
              placeholder={hashtags.length ? '' : '#healing #gratitude #family'}
              disabled={hashtags.length >= 10}
              maxLength={40}
              className="flex-1 min-w-[120px] bg-transparent outline-none text-sm h-7"
            />
          </div>
          <div className="mt-1.5 text-xs text-zinc-500" data-testid="hashtag-count">
            {hashtags.length}/10
          </div>
        </div>
        )}
      </Section>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm" data-testid="submit-error">
          {error}
        </div>
      )}

      <button
        onClick={handleSubmit}
        disabled={!canSubmit}
        data-testid="publish-prayer-btn"
        className="w-full h-11 rounded-full bg-zinc-900 text-white dark:bg-white dark:text-zinc-900 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-2"
      >
        {submitting ? (
          <><Loader2 size={16} className="animate-spin" /> {isEditing ? 'Saving…' : 'Publishing…'}</>
        ) : (
          <><Sparkles size={16} /> {isEditing ? 'Save changes' : 'Save Prayer'}</>
        )}
      </button>

      {/* Method-picker modal — first-step chooser shown on Create.
          Presents four tiles: Video, Voice, Keyboard, Assistance.
          Each tile closes the picker and initiates the chosen flow. */}
      <Dialog open={methodPickerOpen} onOpenChange={setMethodPickerOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="method-picker-modal">
          <DialogHeader>
            <DialogTitle>How would you like to create your prayer?</DialogTitle>
            <DialogDescription>
              Pick any method to start — you can switch later.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            {[
              { key: 'video', icon: Video, label: 'Video', desc: 'Record a 1-minute video.', onPick: () => { setMethodPickerOpen(false); setTimeout(openVideoRecorder, 100); } },
              { key: 'voice', icon: Mic, label: 'Voice', desc: 'Record your voice.', onPick: () => { setMethodPickerOpen(false); setTimeout(openRecorder, 100); } },
              { key: 'keyboard', icon: Keyboard, label: 'Keyboard', desc: 'Type your prayer directly.', onPick: () => { setMethodPickerOpen(false); setTimeout(() => textAreaRef.current?.focus(), 200); } },
              { key: 'assistance', icon: Sparkles, label: 'Assistance', desc: 'Chat with Prayer Assistance to draft it.', onPick: () => { setMethodPickerOpen(false); setTimeout(openGuidedChat, 100); } },
            ].map(({ key, icon: Icon, label, desc, onPick }) => (
              <button
                key={key}
                type="button"
                onClick={onPick}
                data-testid={`method-pick-${key}`}
                className="group flex flex-col items-center text-center gap-2 py-5 px-3 rounded-2xl border border-zinc-200 dark:border-zinc-800 hover:border-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/30 transition-colors"
              >
                <span className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-950/50 text-amber-600 dark:text-amber-300 inline-flex items-center justify-center group-hover:bg-amber-500 group-hover:text-white transition-colors">
                  <Icon size={22} strokeWidth={2.25} />
                </span>
                <div className="font-semibold text-sm text-zinc-900 dark:text-white">{label}</div>
                <div className="text-xs text-zinc-500 dark:text-zinc-400 leading-snug">{desc}</div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Video Recorder Modal — camera capture + Retake/Approve review.
          Kept minimal on purpose: one live-preview <video>, one timer,
          and a two-state footer (recording controls vs. review actions). */}
      <Dialog open={videoRecorderOpen} onOpenChange={(open) => { if (!open) closeVideoRecorder(); }}>
        <DialogContent className="sm:max-w-lg" data-testid="video-recorder-modal">
          <DialogHeader>
            <DialogTitle>Record a video prayer</DialogTitle>
            <DialogDescription>
              Up to 1 minute. Preview your clip before it uploads.
            </DialogDescription>
          </DialogHeader>

          <div className="py-2">
            <div className="relative rounded-xl overflow-hidden bg-black aspect-video">
              <video
                ref={videoElRef}
                data-testid="video-recorder-preview"
                {...(videoPreviewUrl ? { src: videoPreviewUrl, controls: true } : { autoPlay: true, muted: true, playsInline: true })}
                className="w-full h-full object-cover"
              />
              {videoRecording && (
                <div
                  className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-red-600/90 text-white text-xs font-semibold"
                  data-testid="video-recording-indicator"
                >
                  <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                  REC · 0:{String(videoRemainingSec).padStart(2, '0')}
                </div>
              )}
            </div>
          </div>

          {/* Footer — controls vary by phase */}
          <div className="flex items-center justify-end gap-2 pt-2">
            {!videoBlob && !videoRecording && (
              <>
                <button
                  type="button"
                  onClick={closeVideoRecorder}
                  className="h-10 px-4 rounded-full text-sm border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800"
                  data-testid="video-cancel-btn"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={startVideoRecording}
                  data-testid="video-start-btn"
                  className="h-10 px-4 rounded-full text-sm font-semibold bg-amber-500 hover:bg-amber-600 text-white inline-flex items-center gap-2"
                >
                  <Video size={14} /> Start recording
                </button>
              </>
            )}
            {videoRecording && (
              <button
                type="button"
                onClick={stopVideoRecording}
                data-testid="video-stop-btn"
                className="h-10 px-4 rounded-full text-sm font-semibold bg-red-600 hover:bg-red-700 text-white inline-flex items-center gap-2"
              >
                <Square size={14} /> Stop
              </button>
            )}
            {videoBlob && !videoRecording && (
              <>
                <button
                  type="button"
                  onClick={retakeVideo}
                  disabled={videoUploading}
                  data-testid="video-retake-btn"
                  className="h-10 px-4 rounded-full text-sm border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-60"
                >
                  Retake
                </button>
                <button
                  type="button"
                  onClick={approveVideo}
                  disabled={videoUploading}
                  data-testid="video-approve-btn"
                  className="h-10 px-4 rounded-full text-sm font-semibold bg-emerald-500 hover:bg-emerald-600 text-white inline-flex items-center gap-2 disabled:opacity-60"
                >
                  {videoUploading ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                  {videoUploading ? 'Uploading…' : 'Approve'}
                </button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>


      {/* Recorder Modal */}
      <Dialog open={recorderOpen} onOpenChange={(open) => { if (!open) closeRecorder(); }}>
        <DialogContent className="sm:max-w-md" data-testid="recorder-modal">
          <DialogHeader>
            <DialogTitle>Speak your prayer</DialogTitle>
            <DialogDescription>
              We&apos;ll transcribe it and place the words in your prayer text.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {transcribing ? (
              <div className="flex flex-col items-center gap-3 py-6" data-testid="transcribing-state">
                <div className="w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-950/40 flex items-center justify-center">
                  <Loader2 className="animate-spin text-amber-600" size={28} />
                </div>
                <div className="text-sm font-medium">
                  {audioReplacing ? 'Replacing your recording…' : 'Transcribing…'}
                </div>
                <div className="text-xs text-zinc-500 text-center max-w-xs">
                  {audioReplacing
                    ? 'Saving the new audio and refreshing the transcript.'
                    : 'Turning your voice into words. This usually takes a few seconds.'}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-4 py-4">
                <button
                  type="button"
                  onClick={recording ? stopRecording : startRecording}
                  data-testid={recording ? 'stop-recording-btn' : 'start-recording-btn'}
                  className={`w-24 h-24 rounded-full flex items-center justify-center transition-transform active:scale-95 ${
                    recording
                      ? 'bg-red-600 text-white animate-pulse'
                      : 'bg-zinc-900 text-white dark:bg-white dark:text-zinc-900 hover:scale-105'
                  }`}
                  aria-label={recording ? 'Stop recording' : 'Start recording'}
                >
                  {recording ? <Square size={32} /> : <Mic size={32} />}
                </button>
                <div className="text-3xl font-mono tabular-nums" data-testid="recorder-timer">{fmtTime(elapsed)}</div>
                <div className="text-base text-zinc-900 dark:text-zinc-100 text-center">
                  {recording
                    ? `Recording… tap to stop (max. 1 min.)`
                    : 'Tap icon above to record (max. 1 min.)'}
                </div>
              </div>
            )}

            {recorderError && (
              <div className="mt-3 p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs text-center" data-testid="recorder-error">
                {recorderError}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={closeRecorder}
              data-testid="cancel-recorder-btn"
              disabled={transcribing}
              className="h-9 px-4 rounded-md text-sm border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-50 flex items-center gap-1"
            >
              <X size={14} /> Cancel
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Guided prayer — multi-turn OpenAI chat that helps compose a prayer */}
      <Dialog open={guidedOpen} onOpenChange={(o) => {
        if (guidedSending) return;
        setGuidedOpen(o);
        // Closing the modal (backdrop click, Escape, X) wipes chat state so
        // the next open always starts a brand-new session.
        if (!o) resetGuidedChat();
      }}>
        <DialogContent
          className="p-3 sm:p-6 gap-3 sm:gap-4 w-[calc(100vw-1rem)] sm:w-full max-w-[500px] sm:max-w-lg max-h-[calc(100dvh-1rem)] sm:max-h-[85vh] flex flex-col overflow-hidden"
          style={guidedVvStyle}
          data-testid="guided-prayer-modal"
        >
          <DialogHeader className="pr-8">
            <DialogTitle className="flex items-center gap-2 min-w-0">
              <Zap size={18} className="text-amber-500 shrink-0" />
              <span className="truncate">Prayer Assistance</span>
            </DialogTitle>
            {/* Kept for screen-reader users (Radix requires a
                DialogDescription for a11y). Visually hidden — the
                previous "Please use this assistance…" copy was
                removed at the user's request. */}
            <DialogDescription className="sr-only">
              A guided chat that helps you draft your prayer.
            </DialogDescription>
            {guidedMessages.some((m) => {
              if (m.role !== 'assistant' || m.hidden) return false;
              const c = (m.content || '').trim();
              return c.length >= 120 && (looksLikeClosing(c) || PRAYER_END_RE.test(c));
            }) && (
              <DialogDescription className="mt-2" data-testid="guided-prayer-usehint">
                When a draft feels right, tap
                <span className="inline-flex items-center gap-1 mx-1 font-medium text-zinc-900 dark:text-zinc-100">
                  <ClipboardCheck size={12} /> Use prayer
                </span>
                to send it to your prayer text.
              </DialogDescription>
            )}
          </DialogHeader>

          <div
            ref={guidedScrollRef}
            className="flex-1 overflow-y-auto -mx-2 px-2 py-2 space-y-3"
            data-testid="guided-chat-scroll"
          >
            {guidedMessages.filter((m) => !m.hidden).length === 0 && !guidedSending && (
              <div className="py-8 text-center text-sm text-zinc-500" data-testid="guided-empty-state">
                Starting the conversation…
              </div>
            )}
            {guidedMessages.map((m, i) => {
              if (m.hidden) return null;
              const isUser = m.role === 'user';
              // Detect a draft by looking for a tradition-appropriate
              // closing (Amen, Ameen, Om Shanti, Waheguru, Sat Sri Akal,
              // etc.) on the final line — see `looksLikeClosing`. Kept
              // the marker check as a defensive fallback in case an LLM
              // slip still emits `[[PRAYER_END]]`.
              //
              // The length gate below prevents early assistant greetings
              // ("peace be with you", "shalom!", one-liner questions) from
              // being mis-detected as drafts — the "Use prayer" chip only
              // appears once the assistant has produced a substantive
              // prayer body (≥120 chars).
              const rawContent = (m.content || '').trim();
              const looksLikeDraft = !isUser && rawContent.length >= 120 && (
                looksLikeClosing(rawContent)
                || PRAYER_END_RE.test(rawContent)
              );
              const displayContent = stripPrayerEndMarker(m.content);
              return (
                <div
                  key={i}
                  data-testid={`guided-msg-${i}`}
                  className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap break-words ${
                      isUser
                        ? 'bg-zinc-900 text-white dark:bg-white dark:text-zinc-900 rounded-br-sm'
                        : 'bg-amber-50 dark:bg-amber-950/40 text-zinc-800 dark:text-zinc-100 rounded-bl-sm border border-amber-100 dark:border-amber-900/40'
                    }`}
                  >
                    <div className="leading-relaxed">{displayContent}</div>
                    {looksLikeDraft && (
                      <button
                        type="button"
                        onClick={() => applyGuidedReply(m.content)}
                        data-testid={`guided-use-reply-${i}`}
                        className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded-full bg-amber-500 text-white hover:bg-amber-600 active:scale-95 transition"
                      >
                        <ClipboardCheck size={12} /> Use prayer
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            {guidedSending && (
              <div className="flex justify-start" data-testid="guided-typing">
                <div className="max-w-[85%] rounded-2xl px-3.5 py-2 text-sm bg-amber-50 dark:bg-amber-950/40 border border-amber-100 dark:border-amber-900/40 text-zinc-500 dark:text-zinc-400 flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" />
                  {guidedMessages.filter((m) => !m.hidden).length === 0
                    ? 'Opening Prayer Assistance…'
                    : 'The guide is thinking…'}
                </div>
              </div>
            )}
            {guidedError && (
              <div className="p-2 rounded-md bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs" data-testid="guided-error">
                {guidedError}
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="pt-3 border-t border-zinc-200 dark:border-zinc-800 flex items-end gap-2">
            <textarea
              ref={guidedInputRef}
              value={guidedInput}
              onChange={(e) => setGuidedInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendGuidedTurn(guidedInput);
                }
              }}
              onFocus={() => {
                // Force an immediate mobile resize so the dialog
                // shrinks the moment the textarea grabs focus — some
                // mobile browsers (in-app webviews, older Chrome
                // Android) don't fire `visualViewport.resize` fast
                // enough when the keyboard opens. We also retry after
                // 200 ms and 500 ms once the keyboard is fully in so
                // the visible-band measurement is spot-on.
                applyGuidedSizing();
                setTimeout(applyGuidedSizing, 200);
                setTimeout(() => {
                  applyGuidedSizing();
                  // Keep the textarea itself visible above the
                  // keyboard on the final tick.
                  guidedInputRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
                }, 500);
              }}
              onBlur={() => {
                // When the keyboard closes, recompute so the dialog
                // re-expands to the full visible viewport.
                setTimeout(applyGuidedSizing, 200);
              }}
              rows={1}
              placeholder={guidedMessages.length === 0 ? 'Starting…' : 'Reply to the guide…'}
              disabled={guidedSending}
              data-testid="guided-chat-input"
              className="flex-1 min-w-0 max-h-32 resize-none px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-transparent text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/60 focus:border-amber-400/70 focus:shadow-[0_0_0_4px_rgba(251,191,36,0.15)] sm:focus:shadow-none transition-shadow disabled:opacity-50"
            />
            <button
              type="button"
              onClick={() => sendGuidedTurn(guidedInput)}
              disabled={guidedSending || !guidedInput.trim()}
              data-testid="guided-send-btn"
              className="h-10 w-10 shrink-0 rounded-full bg-zinc-900 text-white dark:bg-white dark:text-zinc-900 flex items-center justify-center disabled:opacity-40"
            >
              {guidedSending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </div>

          <div className="flex justify-between items-center pt-2">
            <button
              type="button"
              onClick={() => {
                // Rotate session + clear history; the useEffect that
                // watches guidedOpen/guidedSessionId will fire the fresh
                // seed once React commits — no closure-race risk.
                guidedSeedFiredRef.current = false;
                resetGuidedChat();
              }}
              disabled={guidedSending || guidedMessages.length === 0}
              data-testid="guided-reset-btn"
              className="text-xs text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200 flex items-center gap-1 disabled:opacity-40"
            >
              <RefreshCw size={12} /> Start over
            </button>
            <button
              type="button"
              onClick={() => { setGuidedOpen(false); resetGuidedChat(); }}
              disabled={guidedSending}
              data-testid="close-guided-prayer-btn"
              className="h-8 px-3 rounded-md text-sm border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-50"
            >
              Close
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sunrise / Sunset gallery */}
      <GalleryPickerModal
        open={galleryOpen}
        onOpenChange={setGalleryOpen}
        onPick={handleGalleryPick}
      />
    </div>
  );
};

export default AddPrayer;
